/**
 * A function to create a log object for the 'disconnectReasonSelected' event (evid: 102)
 *
 * Event description: Merchant select reason when disconnect WP
 *
 * @param {disconnectReasonSelectedParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {string} modal_name - disconnect_last_provider_popup / switch_provider etc
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} page
 * @property {string} paymentCategory - currently connected category
 * @property {string} paymentProvider - currently connected provider
 * @property {string} platformName
 * @property {string} reason - reason selected or user input
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<disconnectReasonSelectedParams>} LogObject<disconnectReasonSelectedParams>
 */
export function disconnectReasonSelected(params) {
    return { evid: 102, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayCallBackReceived' event (evid: 264)
 *
 * Event description: When user change some data on ApplePay side
 *
 * @param {applePayCallBackReceivedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {number} amountLong - new amount * 100000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} fieldName - what was changed
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} newValue - value that was selected
 * @property {string} paymentProvider - payment_provider
 * @property {string} paymentmethod - payment method that was choosed
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session for ECW
 * @property {GUID} visitorId
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayCallBackReceivedParams>} LogObject<applePayCallBackReceivedParams>
 */
export function applePayCallBackReceived(params) {
    return { evid: 264, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'businessLocationConfirmLoaded' event (evid: 190)
 *
 * Event description: Pop-up with @Confirm location@ CTA loaded
 *
 * @param {businessLocationConfirmLoadedParams} params - The parameters for this event
 * @property {string} device - device_type
 * @property {boolean} isPartner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} msid - MSID
 * @property {boolean} status - status
 * @property {GUID} userGuid - UUID
 * @property {GUID} visitorId
 * @return {LogObject<businessLocationConfirmLoadedParams>} LogObject<businessLocationConfirmLoadedParams>
 */
export function businessLocationConfirmLoaded(params) {
    return { evid: 190, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'buyerChoosesPaymentMethod' event (evid: 105)
 *
 * Event description: Buyer chooses payment method: Offline payment, MercadoPago, Paypal, Credit card
 *
 * @param {buyerChoosesPaymentMethodParams} params - The parameters for this event
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - -
 * @property {string} captchaRequiredForPM - payment method which requires passing captcha challenge (e.i. creditCard))
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {GUID} creditCardId - null if visitor doesn't have saved cc
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {string} mandatoryFieldsList - list of required mandatory fields that were shown to UoU
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {GUID} membershipId
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - msid
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} passedMandatoryFields - list of required mandatory fields  that weren't shown to UoU and were passed by vertical
 * @property {string} paymentAgreementId - null if visitor doesn't have saved payment agreements
 * @property {string} paymentCategory - payment_category
 * @property {string} paymentProvider - payment_provider
 * @property {string} paymentMethod - -
 * @property {string} prefilledMandatoryFields - list of required mandatory fields  that were shown to UoU already prefilled
 * @property {string} requiredPmMandatoryFields - list of required mandatory fields from payment provider
 * @property {string} savePaymentMethodDisplayOption - if  pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<buyerChoosesPaymentMethodParams>} LogObject<buyerChoosesPaymentMethodParams>
 */
export function buyerChoosesPaymentMethod(params) {
    return { evid: 105, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickUnsuspendAccount' event (evid: 417)
 *
 * Event description: Employee click  unsuspend account
 *
 * @param {boAccountPageClickUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickUnsuspendAccountParams>} LogObject<boAccountPageClickUnsuspendAccountParams>
 */
export function boAccountPageClickUnsuspendAccount(params) {
    return { evid: 417, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageUnsuspendAccountOpened' event (evid: 436)
 *
 * Event description: Employee open unsuspend account
 *
 * @param {boAccountPageUnsuspendAccountOpenedParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - load time
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId - visitor id
 * @return {LogObject<boAccountPageUnsuspendAccountOpenedParams>} LogObject<boAccountPageUnsuspendAccountOpenedParams>
 */
export function boAccountPageUnsuspendAccountOpened(params) {
    return { evid: 436, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardFilterApplied' event (evid: 157)
 *
 * Event description: one of filters is applied (Currency / App / Status / Payment Method)
 *
 * @param {paymentDashboardFilterAppliedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} selectedValue - what was exactly selected
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardFilterAppliedParams>} LogObject<paymentDashboardFilterAppliedParams>
 */
export function paymentDashboardFilterApplied(params) {
    return { evid: 157, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickCompleteSetUpWp' event (evid: 244)
 *
 * Event description: Merchant clicked complete set-up WP on cashier payment method page
 * substitution of event 85:116
 *
 * @param {merchantClickCompleteSetUpWpParams} params - The parameters for this event
 * @property {GUID} accountProfileId - wp profile id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - what page in the dashboard / origin page
 * @property {GUID} visitorId
 * @property {string} wpGeo - wp geo
 * @return {LogObject<merchantClickCompleteSetUpWpParams>} LogObject<merchantClickCompleteSetUpWpParams>
 */
export function merchantClickCompleteSetUpWp(params) {
    return { evid: 244, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnRemoveFromPaymentMethodsPage' event (evid: 146)
 *
 * Event description: Merchant- click on remove from Payment Methods page in the not connected list
 *
 * @param {merchantClickOnRemoveFromPaymentMethodsPageParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - payment provider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickOnRemoveFromPaymentMethodsPageParams>} LogObject<merchantClickOnRemoveFromPaymentMethodsPageParams>
 */
export function merchantClickOnRemoveFromPaymentMethodsPage(params) {
    return { evid: 146, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodePaymentCreationStarted' event (evid: 206)
 *
 * Event description: Payment (Order) creation started after buyer initiates it (button click etc)
 *
 * @param {wixCodePaymentCreationStartedParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - INIT ONLY
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - NOT IN USE: order_snapshot_id
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodePaymentCreationStartedParams>} LogObject<wixCodePaymentCreationStartedParams>
 */
export function wixCodePaymentCreationStarted(params) {
    return { evid: 206, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'installmentBannerListLoaded' event (evid: 260)
 *
 * Event description: Widget with data about installments ( Afterpay, laybuy, etc) has changed because of buyer's change of the order amount
 *
 * @param {installmentBannerListLoadedParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - m
 * @property {string} offerId - offered payment methods that were shown (array)
 * @property {string} paymentProvider - offered payment providers that were shown (array)
 * @property {GUID} visitorId
 * @return {LogObject<installmentBannerListLoadedParams>} LogObject<installmentBannerListLoadedParams>
 */
export function installmentBannerListLoaded(params) {
    return { evid: 260, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boMainPageSearchResult' event (evid: 402)
 *
 * Event description: Employee gets search results
 *
 * @param {boMainPageSearchResultParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {string} errorMessage - error if search was failed
 * @property {boolean} isPartner
 * @property {number} loadTime - how long users wait for result
 * @property {GUID} msid
 * @property {number} returnedResults - count of returned results
 * @property {string} searchType - email, url, msid, uuid, etc
 * @property {GUID} visitorId
 * @return {LogObject<boMainPageSearchResultParams>} LogObject<boMainPageSearchResultParams>
 */
export function boMainPageSearchResult(params) {
    return { evid: 402, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsPageScrolled' event (evid: 464)
 *
 * Event description: Merchant scrolled Accept Payments Page
 *
 * @param {merchantAcceptPaymentsPageScrolledParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {number} pageHeight - max page Y axis in pixels
 * @property {string} paymentCategory - array of connected payment_methods
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} screenResolution - screen resolution in pixels (width x height)
 * @property {number} screenScrolledPercentageEnd - end position (lower bound)
 * @property {number} screenScrolledPercentageStarting - start position (lower bound)
 * @property {GUID} userGuid - user_guid
 * @property {number} viewportHeight - viewport height in pixels
 * @property {GUID} visitorId
 * @return {LogObject<merchantAcceptPaymentsPageScrolledParams>} LogObject<merchantAcceptPaymentsPageScrolledParams>
 */
export function merchantAcceptPaymentsPageScrolled(params) {
    return { evid: 464, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantVerifyAccountClicked277' event (evid: 277)
 *
 * Event description: Click on Verify Account on Accept Payment
 *
 * @param {merchantVerifyAccountClicked277Params} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - page where user at
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantVerifyAccountClicked277Params>} LogObject<merchantVerifyAccountClicked277Params>
 */
export function merchantVerifyAccountClicked277(params) {
    return { evid: 277, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantTooltipWasShownToUser' event (evid: 465)
 *
 * Event description: Tooltip was shown to user on accept_payments / manage_page / connect_page / third_party_cc_providers
 *
 * @param {merchantTooltipWasShownToUserParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} page - page where tooltip appear
 * @property {string} paymentCategory - currently connected category
 * @property {string} paymentProvider - currently connected provider
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} tooltipName - e.g. connected / not_connected / wix_pos etc
 * @property {string} tooltipText - e.g. "These payment options are connected and available to your customers" etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - guid
 * @return {LogObject<merchantTooltipWasShownToUserParams>} LogObject<merchantTooltipWasShownToUserParams>
 */
export function merchantTooltipWasShownToUser(params) {
    return { evid: 465, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutThankYouPageLoad' event (evid: 267)
 *
 * Event description: Thank You Page was shown to the visitor after the order completed for InApp browser
 *
 * @param {checkoutThankYouPageLoadParams} params - The parameters for this event
 * @property {string} amount - checkout amount
 * @property {string} appId - app_id
 * @property {string} appInstanceId - app_instance_id
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} transactionStatus - transaction status
 * @property {string} uuid - uuid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutThankYouPageLoadParams>} LogObject<checkoutThankYouPageLoadParams>
 */
export function checkoutThankYouPageLoad(params) {
    return { evid: 267, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbAnyErrorDuringGpayFlow' event (evid: 463)
 *
 * Event description: Fires in case of any other errors which can occur during payment flow through Gpay
 *
 * @param {spbAnyErrorDuringGpayFlowParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} errorReason - Error reason
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {string} productId - product id
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbAnyErrorDuringGpayFlowParams>} LogObject<spbAnyErrorDuringGpayFlowParams>
 */
export function spbAnyErrorDuringGpayFlow(params) {
    return { evid: 463, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodePaymentCreationCompleted' event (evid: 207)
 *
 * Event description: Payment (Order) creation completed
 *
 * @param {wixCodePaymentCreationCompletedParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {number} duration - loading time in miliseconds
 * @property {string} errorDesc - in case of error
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - INIT ONLY
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {boolean} status - creation status: true / false
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodePaymentCreationCompletedParams>} LogObject<wixCodePaymentCreationCompletedParams>
 */
export function wixCodePaymentCreationCompleted(params) {
    return { evid: 207, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnAdd' event (evid: 143)
 *
 * Event description: Merchant-click on +add  on the second page of the payment methods
 *
 * @param {merchantClickOnAddParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} origin - notification/button
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantClickOnAddParams>} LogObject<merchantClickOnAddParams>
 */
export function merchantClickOnAdd(params) {
    return { evid: 143, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletAddCcButtonClicked' event (evid: 443)
 *
 * Event description: 'Add CC' button  was clicked by member
 *
 * @param {myWalletAddCcButtonClickedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitorId
 * @return {LogObject<myWalletAddCcButtonClickedParams>} LogObject<myWalletAddCcButtonClickedParams>
 */
export function myWalletAddCcButtonClicked(params) {
    return { evid: 443, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantUnselectPaymentCategoryToConnect' event (evid: 138)
 *
 * Event description: Merchant-unselect payment category to connect
 *
 * @param {merchantUnselectPaymentCategoryToConnectParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} origin - - not in use
 * @property {string} pageName - Payment Method_first page
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantUnselectPaymentCategoryToConnectParams>} LogObject<merchantUnselectPaymentCategoryToConnectParams>
 */
export function merchantUnselectPaymentCategoryToConnect(params) {
    return { evid: 138, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundComfirmed' event (evid: 411)
 *
 * Event description: Employee choose amount and confirm refund
 *
 * @param {boSitePageTransactionRefundComfirmedParams} params - The parameters for this event
 * @property {number} amount - refunded amount
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} reason - reason
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageTransactionRefundComfirmedParams>} LogObject<boSitePageTransactionRefundComfirmedParams>
 */
export function boSitePageTransactionRefundComfirmed(params) {
    return { evid: 411, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletModalFinishedLoading' event (evid: 445)
 *
 * Event description: Modal window finished loading on wallet page (depend on modal_type: 'Adding CC', 'Deletion CC')
 *
 * @param {myWalletModalFinishedLoadingParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} cardId - card_guid
 * @property {number} duration - milliseconds from load started to load finished
 * @property {boolean} isPartner
 * @property {string} modalType - modal type: 'Adding CC', 'Deletion CC'
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<myWalletModalFinishedLoadingParams>} LogObject<myWalletModalFinishedLoadingParams>
 */
export function myWalletModalFinishedLoading(params) {
    return { evid: 445, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionDrawerClose' event (evid: 409)
 *
 * Event description: Employee close transaction drawer
 *
 * @param {boSitePageTransactionDrawerCloseParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageTransactionDrawerCloseParams>} LogObject<boSitePageTransactionDrawerCloseParams>
 */
export function boSitePageTransactionDrawerClose(params) {
    return { evid: 409, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletModalClosed' event (evid: 448)
 *
 * Event description: Any modal is closed by member (click on cross X)
 *
 * @param {myWalletModalClosedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} cardId - card_guid
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} isPartner
 * @property {string} modalType - modal type: 'Adding CC', 'Deletion CC'
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitorId
 * @return {LogObject<myWalletModalClosedParams>} LogObject<myWalletModalClosedParams>
 */
export function myWalletModalClosed(params) {
    return { evid: 448, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardViewMoreTransactionsClicked' event (evid: 159)
 *
 * Event description: when user clicks to view 10 more transactions in the end of the list
 *
 * @param {paymentDashboardViewMoreTransactionsClickedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {number} transactionsShown - current number of visible transactions per page
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardViewMoreTransactionsClickedParams>} LogObject<paymentDashboardViewMoreTransactionsClickedParams>
 */
export function paymentDashboardViewMoreTransactionsClicked(params) {
    return { evid: 159, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnCanelInTheUpdateModal' event (evid: 141)
 *
 * Event description: Merchant--click on canel in the update modal- General event for click on cancel or exsit in the modal
 *
 * @param {merchantClickOnCanelInTheUpdateModalParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} pageName - add more payment methods
 * @property {string} paymentCategory - paymentCategory
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantClickOnCanelInTheUpdateModalParams>} LogObject<merchantClickOnCanelInTheUpdateModalParams>
 */
export function merchantClickOnCanelInTheUpdateModal(params) {
    return { evid: 141, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmRefundPayout' event (evid: 426)
 *
 * Event description: Employee click Confirm    Refund Payout
 *
 * @param {boAccountPageClickConfirmRefundPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {number} amount - payout amount
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} reason - reason
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickConfirmRefundPayoutParams>} LogObject<boAccountPageClickConfirmRefundPayoutParams>
 */
export function boAccountPageClickConfirmRefundPayout(params) {
    return { evid: 426, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickFixIssue' event (evid: 103)
 *
 * Event description: User clicks on 'Fix Issue' with Wix Payments accounts
 *
 * @param {merchantClickFixIssueParams} params - The parameters for this event
 * @property {string} bannerName - i.e. wix_payments_card_notification
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - provider under wp
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantClickFixIssueParams>} LogObject<merchantClickFixIssueParams>
 */
export function merchantClickFixIssue(params) {
    return { evid: 103, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodePaymentInitializationStarted' event (evid: 208)
 *
 * Event description: Payment Initialization started
 *
 * @param {wixCodePaymentInitializationStartedParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {number} duration - NOT IN USE: loading time in miliseconds from buyer activity to page started loading
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {boolean} status - NOT IN USE: creation status: true / false
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodePaymentInitializationStartedParams>} LogObject<wixCodePaymentInitializationStartedParams>
 */
export function wixCodePaymentInitializationStarted(params) {
    return { evid: 208, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutPaymentComponentShown' event (evid: 180)
 *
 * Event description: Payment Component is shown to a buyer
 *
 * @param {checkoutPaymentComponentShownParams} params - The parameters for this event
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - react if new
 * @property {string} captchaRequiredForPM - list of payment methods which require passing captcha challenge (e.i. creditCard))
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {GUID} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {number} hasSavedCreditCards - null if visitor is not logged in or doesn't have saved cc
 * @property {number} hasSavedPaymentAgreements - null if visitor is not logged in or doesn't have saved payment agreements
 * @property {number} hasSavedPaymentMethods - null if visitor is not logged in or doesn't have saved payment methods
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {boolean} isPartner
 * @property {string} mandatoryFieldsList - list of required mandatory fields that were shown to UoU
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {GUID} membershipId
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} passedMandatoryFields - list of required mandatory fields  that weren't shown to UoU and were passed by vertical
 * @property {string} payWith - default payment category
 * @property {string} paymentAgreementList - list of payment agreements which were received
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} prefilledMandatoryFields - list of required mandatory fields  that were shown to UoU already prefilled
 * @property {string} requiredPmMandatoryFields - list of required mandatory fields from payment provider
 * @property {string} savePaymentMethodDisplayOption - if  pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutPaymentComponentShownParams>} LogObject<checkoutPaymentComponentShownParams>
 */
export function checkoutPaymentComponentShown(params) {
    return { evid: 180, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelUnsuspendAccount' event (evid: 419)
 *
 * Event description: Employee click Cancel unsuspend account
 *
 * @param {boAccountPageClickCancelUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickCancelUnsuspendAccountParams>} LogObject<boAccountPageClickCancelUnsuspendAccountParams>
 */
export function boAccountPageClickCancelUnsuspendAccount(params) {
    return { evid: 419, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'installmentBannerLoadStart' event (evid: 257)
 *
 * Event description: start loading widget with data about installments ( Afterpay, laybuy, etc)
 *
 * @param {installmentBannerLoadStartParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} productId - product id
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<installmentBannerLoadStartParams>} LogObject<installmentBannerLoadStartParams>
 */
export function installmentBannerLoadStart(params) {
    return { evid: 257, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boMainPageOpen' event (evid: 400)
 *
 * Event description: Employee open Main Page in Back Office
 *
 * @param {boMainPageOpenParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {GUID} visitorId
 * @return {LogObject<boMainPageOpenParams>} LogObject<boMainPageOpenParams>
 */
export function boMainPageOpen(params) {
    return { evid: 400, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbButtonStartInitializationCheckout' event (evid: 213)
 *
 * Event description: Smart Payment Button start Initialization Checkout (start creating order on PayPal)
 *
 * @param {spbButtonStartInitializationCheckoutParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbButtonStartInitializationCheckoutParams>} LogObject<spbButtonStartInitializationCheckoutParams>
 */
export function spbButtonStartInitializationCheckout(params) {
    return { evid: 213, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantEnableAdvancedOptionsClicked' event (evid: 163)
 *
 * Event description: Click enable advance options in Payment Methods Page - in order to grant permissions for recurring payment
 *
 * @param {merchantEnableAdvancedOptionsClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantEnableAdvancedOptionsClickedParams>} LogObject<merchantEnableAdvancedOptionsClickedParams>
 */
export function merchantEnableAdvancedOptionsClicked(params) {
    return { evid: 163, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantModalWasShownToUser' event (evid: 248)
 *
 * Event description: Modal was shown to user on accept_payments / manage_page / third_party_cc_providers
 *
 * @param {merchantModalWasShownToUserParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {string} modal_name - disconnect_last_provider_popup / switch_provider etc
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} page - page where modal appear
 * @property {string} paymentCategory - currently connected category
 * @property {string} paymentProvider - currently connected provider
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantModalWasShownToUserParams>} LogObject<merchantModalWasShownToUserParams>
 */
export function merchantModalWasShownToUser(params) {
    return { evid: 248, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayShippingAddressNotAccepted' event (evid: 106)
 *
 * Event description: if Shipping addres is not OK
 *
 * @param {applePayShippingAddressNotAcceptedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {number} amountLong - new amount ( * 100 000)
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - new value
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentMethod - new method, when method was changed
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId
 * @property {GUID} visitorId - visitor id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayShippingAddressNotAcceptedParams>} LogObject<applePayShippingAddressNotAcceptedParams>
 */
export function applePayShippingAddressNotAccepted(params) {
    return { evid: 106, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutMitLegalStatementFlow' event (evid: 459)
 *
 * Event description: Applied for MIT trx, once merchant undergo payment flow, he/she is supposed to see legal statement, can check chargeback liability and cards on file and download authorization form
 *
 * @param {checkoutMitLegalStatementFlowParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {GUID} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {boolean} isDefaultLanguage - true/false for step redirect_to_download
 * @property {boolean} isPartner - is_partner
 * @property {string} language - selected language in dropdown
 * @property {GUID} membershipId - membership_id
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} searchResults - result of search
 * @property {string} stepName - steps of MIT Legal statement flow: render_statement, click_chargeback, click_download
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutMitLegalStatementFlowParams>} LogObject<checkoutMitLegalStatementFlowParams>
 */
export function checkoutMitLegalStatementFlow(params) {
    return { evid: 459, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmUnsuspendAccount' event (evid: 418)
 *
 * Event description: Employee click Confirm  unsuspend account
 *
 * @param {boAccountPageClickConfirmUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickConfirmUnsuspendAccountParams>} LogObject<boAccountPageClickConfirmUnsuspendAccountParams>
 */
export function boAccountPageClickConfirmUnsuspendAccount(params) {
    return { evid: 418, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickProcessPayout' event (evid: 421)
 *
 * Event description: Employee click   Process Payout in account
 *
 * @param {boAccountPageClickProcessPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickProcessPayoutParams>} LogObject<boAccountPageClickProcessPayoutParams>
 */
export function boAccountPageClickProcessPayout(params) {
    return { evid: 421, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentModalClosed' event (evid: 212)
 *
 * Event description: User click to close payment external modal window/tab
 *
 * @param {paymentModalClosedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<paymentModalClosedParams>} LogObject<paymentModalClosedParams>
 */
export function paymentModalClosed(params) {
    return { evid: 212, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageClickUserDrawer' event (evid: 404)
 *
 * Event description: Employee click user drawer
 *
 * @param {boSitePageClickUserDrawerParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageClickUserDrawerParams>} LogObject<boSitePageClickUserDrawerParams>
 */
export function boSitePageClickUserDrawer(params) {
    return { evid: 404, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayStartMerchantValidation' event (evid: 110)
 *
 * Event description: ApplePay startMerchantValidation
 *
 * @param {applePayStartMerchantValidationParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentProvider - payment_provider that chosen (Wixpayments etc.)
 * @property {string} paymentmethod - ApplePay
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayStartMerchantValidationParams>} LogObject<applePayStartMerchantValidationParams>
 */
export function applePayStartMerchantValidation(params) {
    return { evid: 110, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantPaymentDashboardRemovedPopUp' event (evid: 195)
 *
 * Event description: When users, who saw payment dashboard previously, goes to Accept Payment Tab we notify him that place changes
 *
 * @param {merchantPaymentDashboardRemovedPopUpParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {GUID} uuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantPaymentDashboardRemovedPopUpParams>} LogObject<merchantPaymentDashboardRemovedPopUpParams>
 */
export function merchantPaymentDashboardRemovedPopUp(params) {
    return { evid: 195, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSelectUnselectAdditionalPaymentMethodAtManageAccount' event (evid: 199)
 *
 * Event description: Merchant select/unselect additional provider settings at Manage account tab
 *
 * @param {merchantSelectUnselectAdditionalPaymentMethodAtManageAccountParams} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentMethod - creditCard,paypal
 * @property {string} paymentProvider - payment provider
 * @property {boolean} selected - Select/unselect options at Manage account tab. True=selected, false=unselected
 * @property {string} selectedValue - e.g. PayPal Credit
 * @property {GUID} uuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<merchantSelectUnselectAdditionalPaymentMethodAtManageAccountParams>} LogObject<merchantSelectUnselectAdditionalPaymentMethodAtManageAccountParams>
 */
export function merchantSelectUnselectAdditionalPaymentMethodAtManageAccount(params) {
    return { evid: 199, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkOutPaymentMethodValidationCompleted' event (evid: 165)
 *
 * Event description: The result of Payment Method Validation
 *
 * @param {checkOutPaymentMethodValidationCompletedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyercomponent - buyercomponent
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} memberId - member_id
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentCategory - payment_category
 * @property {string} paymentProvider - payment_provider
 * @property {string} paymentMethod
 * @property {string} reason - list of fields with the problem in case when status = false
 * @property {boolean} status - true if success
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkOutPaymentMethodValidationCompletedParams>} LogObject<checkOutPaymentMethodValidationCompletedParams>
 */
export function checkOutPaymentMethodValidationCompleted(params) {
    return { evid: 165, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickRefundPayout' event (evid: 425)
 *
 * Event description: Employee click    Refund Payout in account
 *
 * @param {boAccountPageClickRefundPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickRefundPayoutParams>} LogObject<boAccountPageClickRefundPayoutParams>
 */
export function boAccountPageClickRefundPayout(params) {
    return { evid: 425, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletAddCcButtonRendered' event (evid: 442)
 *
 * Event description: 'Add CC' button  was rendered
 *
 * @param {myWalletAddCcButtonRenderedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {boolean} isPartner
 * @property {number} loadTime - time from initial to render
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitorId
 * @return {LogObject<myWalletAddCcButtonRenderedParams>} LogObject<myWalletAddCcButtonRenderedParams>
 */
export function myWalletAddCcButtonRendered(params) {
    return { evid: 442, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbButtonRendered' event (evid: 210)
 *
 * Event description: Smart Payment Button was rendered
 *
 * @param {spbButtonRenderedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {number} loadTime - time from initial to render
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {string} productId - product id where button was rendered
 * @property {string} renderPlace - place on which button was rendered
 * @property {boolean} requestShipping - if Stores ask about shipping address
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbButtonRenderedParams>} LogObject<spbButtonRenderedParams>
 */
export function spbButtonRendered(params) {
    return { evid: 210, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnEditAccountDetails' event (evid: 269)
 *
 * Event description: Click on Edit Account details at Wix Payments Manage Page
 *
 * @param {merchantClickOnEditAccountDetailsParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {string} accountStatus - accepting_payments, etc
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - manage_page
 * @property {string} paymentCategory - creditCard,cardReader etc
 * @property {string} paymentProvider - wixpay_adyen, wixpay_stripe
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralinfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickOnEditAccountDetailsParams>} LogObject<merchantClickOnEditAccountDetailsParams>
 */
export function merchantClickOnEditAccountDetails(params) {
    return { evid: 269, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantContinueToChooseMethodsForConnect' event (evid: 184)
 *
 * Event description: Merchant clicks Continue on Welcome screen. If he chooses at least 1 online pm with autoconnect, he will be leaded to next screen
 *
 * @param {merchantContinueToChooseMethodsForConnectParams} params - The parameters for this event
 * @property {GUID} appId - cashier app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} availableValue - list of available payment methods
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} defaultValue - list of selected payment methods checked by default
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} selectedValue - list of selected payment methods to continue
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantContinueToChooseMethodsForConnectParams>} LogObject<merchantContinueToChooseMethodsForConnectParams>
 */
export function merchantContinueToChooseMethodsForConnect(params) {
    return { evid: 184, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmProcessPayout' event (evid: 422)
 *
 * Event description: Employee click Confirm   Process Payout
 *
 * @param {boAccountPageClickConfirmProcessPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {number} amount - payout amount
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickConfirmProcessPayoutParams>} LogObject<boAccountPageClickConfirmProcessPayoutParams>
 */
export function boAccountPageClickConfirmProcessPayout(params) {
    return { evid: 422, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutBuyerMovesInAFunnel' event (evid: 202)
 *
 * Event description: Wix Code Check out - Buyer moves in a funnel
 *
 * @param {wixCodeCheckOutBuyerMovesInAFunnelParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} direction - button name (incl. close)
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} page - name of the page
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} pageTo - page buyer will be navigated to
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} selectedCountry - country chosen by buyer
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodeCheckOutBuyerMovesInAFunnelParams>} LogObject<wixCodeCheckOutBuyerMovesInAFunnelParams>
 */
export function wixCodeCheckOutBuyerMovesInAFunnel(params) {
    return { evid: 202, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardTabSelected' event (evid: 155)
 *
 * Event description: Merchant - Click Accept Terms and Conditions TOS notification at WP settings
 *
 * @param {paymentDashboardTabSelectedParams} params - The parameters for this event
 * @property {string} accountStatus - wix payments account status
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} pageType - wp_settings/ payouts / accept_payments, restricted_products
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} referralinfo - place from which user came
 * @property {string} viewMode - siteLevel'/ 'accountLevel' etc
 * @property {GUID} visitorId - visitor_id
 * @property {string} wpGeo - GEO
 * @return {LogObject<paymentDashboardTabSelectedParams>} LogObject<paymentDashboardTabSelectedParams>
 */
export function paymentDashboardTabSelected(params) {
    return { evid: 155, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boletoPrintButton' event (evid: 234)
 *
 * Event description: When User click print boleto
 *
 * @param {boletoPrintButtonParams} params - The parameters for this event
 * @property {number} amountLong - amount
 * @property {DateTime} due_date - due_date on boleto
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boletoPrintButtonParams>} LogObject<boletoPrintButtonParams>
 */
export function boletoPrintButton(params) {
    return { evid: 234, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbShippingMethodChanged' event (evid: 198)
 *
 * Event description: in case we want to know when user change shipping address or payment method on provider side
 *
 * @param {spbShippingMethodChangedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {number} amountLong - new amount ( * 100 000)
 * @property {GUID} appInstanceId - vertical instance id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - new value
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {string} fieldChanged - field which was changed on payment method page
 * @property {boolean} isPartner
 * @property {GUID} msid - site id
 * @property {string} paymentMethod - new method, when method was changed
 * @property {string} paymentProvider - payment_provider
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbShippingMethodChangedParams>} LogObject<spbShippingMethodChangedParams>
 */
export function spbShippingMethodChanged(params) {
    return { evid: 198, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantWixPaymentsLbInteraction' event (evid: 169)
 *
 * Event description: when user interacts with Wix Payments LB
 *
 * @param {merchantWixPaymentsLbInteractionParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} interaction - close / see_all_methods / create_account
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantWixPaymentsLbInteractionParams>} LogObject<merchantWixPaymentsLbInteractionParams>
 */
export function merchantWixPaymentsLbInteraction(params) {
    return { evid: 169, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardRefundRequestConfirmed' event (evid: 161)
 *
 * Event description: user confirms that he wants to refund transaction
 *
 * @param {paymentDashboardRefundRequestConfirmedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} transactionId - transaction_id
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardRefundRequestConfirmedParams>} LogObject<paymentDashboardRefundRequestConfirmedParams>
 */
export function paymentDashboardRefundRequestConfirmed(params) {
    return { evid: 161, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickContinueAtPaymentOptions' event (evid: 275)
 *
 * Event description: Merchant Clicks Continue at Payment Options when connecting Wix Payments
 *
 * @param {merchantClickContinueAtPaymentOptionsParams} params - The parameters for this event
 * @property {string} avalableCategory - available payment methods  (creditCard/cardReader/applePay)
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - payment_options
 * @property {string} paymentProvider - wixpay_adyen/wixpay_stripe
 * @property {string} referralInfo - place from which user came
 * @property {string} selectedCategory - selected payment method (creditCard/cardReader)
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickContinueAtPaymentOptionsParams>} LogObject<merchantClickContinueAtPaymentOptionsParams>
 */
export function merchantClickContinueAtPaymentOptions(params) {
    return { evid: 275, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'pixModalClosed' event (evid: 288)
 *
 * Event description: When User close PIX popup
 *
 * @param {pixModalClosedParams} params - The parameters for this event
 * @property {number} amountLong - amount
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {string} paymentMethodType - payment_method_type
 * @property {string} paymentProvider - Payment Provider
 * @property {string} reason - close modal reason ( Success, Decline, Timeout)
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<pixModalClosedParams>} LogObject<pixModalClosedParams>
 */
export function pixModalClosed(params) {
    return { evid: 288, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSelectPmUnderWixPayment' event (evid: 268)
 *
 * Event description: Merchant click on the toggle to select/unselect additional Payment Method when connecting Wix Payments
 *
 * @param {merchantSelectPmUnderWixPaymentParams} params - The parameters for this event
 * @property {string} coupledCategory - payment category that will be auto selected with current one
 * @property {boolean} isPartner
 * @property {boolean} isSelected - true/false
 * @property {GUID} msid
 * @property {string} pageType - payment_options
 * @property {string} paymentCategory - creditCard,cardReader,applePay
 * @property {string} paymentProvider - wixpay_stripe
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantSelectPmUnderWixPaymentParams>} LogObject<merchantSelectPmUnderWixPaymentParams>
 */
export function merchantSelectPmUnderWixPayment(params) {
    return { evid: 268, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageCloseUserDrawer' event (evid: 405)
 *
 * Event description: Employee click close in user drawer
 *
 * @param {boSitePageCloseUserDrawerParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageCloseUserDrawerParams>} LogObject<boSitePageCloseUserDrawerParams>
 */
export function boSitePageCloseUserDrawer(params) {
    return { evid: 405, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantCompleteSetUpLearnMoreClicked' event (evid: 154)
 *
 * Event description: Merchant click on setup account to activate in smart ob - different sources
 *
 * @param {merchantCompleteSetUpLearnMoreClickedParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - autoconnect / add provider / set up / set up to activate / change provider
 * @property {string} paymentCategory - Credit Card,PayPal,Mercado Pago, Offline
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantCompleteSetUpLearnMoreClickedParams>} LogObject<merchantCompleteSetUpLearnMoreClickedParams>
 */
export function merchantCompleteSetUpLearnMoreClicked(params) {
    return { evid: 154, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClicksOnPublishSite' event (evid: 283)
 *
 * Event description: User clicks on Publish site on the banner in order to be verified
 *
 * @param {merchantClicksOnPublishSiteParams} params - The parameters for this event
 * @property {string} bannerName - wix_payments_card_notification
 * @property {string} cta - publish
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} notificationType - notification name
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClicksOnPublishSiteParams>} LogObject<merchantClicksOnPublishSiteParams>
 */
export function merchantClicksOnPublishSite(params) {
    return { evid: 283, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantCtaAtProhibitedProductsPageClicked' event (evid: 240)
 *
 * Event description: Merchant - CTA at Prohibited products page clicked
 *
 * @param {merchantCtaAtProhibitedProductsPageClickedParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} ctaType - yes/no/learn_more_cbd/learn_more_prohibited/back/close
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} origin - place where user clicks on cta
 * @property {string} pageType - accept_payments / prohibited_products
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantCtaAtProhibitedProductsPageClickedParams>} LogObject<merchantCtaAtProhibitedProductsPageClickedParams>
 */
export function merchantCtaAtProhibitedProductsPageClicked(params) {
    return { evid: 240, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantWixPaymentsLbShown' event (evid: 168)
 *
 * Event description: The LB is shown to Brazilian users, who doesn't have any payment settings (connected or selected payment methods), on opening Accept Payments tab (so they don't see full list of available payment methods)
 *
 * @param {merchantWixPaymentsLbShownParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantWixPaymentsLbShownParams>} LogObject<merchantWixPaymentsLbShownParams>
 */
export function merchantWixPaymentsLbShown(params) {
    return { evid: 168, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'fastCheckoutButtonClicked' event (evid: 192)
 *
 * Event description: when users click "Checkout with ..." on stores cart page. Any provider
 *
 * @param {fastCheckoutButtonClickedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {string} productid - product id where button was clicked
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<fastCheckoutButtonClickedParams>} LogObject<fastCheckoutButtonClickedParams>
 */
export function fastCheckoutButtonClicked(params) {
    return { evid: 192, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'upgradePopupPaymentTabInteraction' event (evid: 151)
 *
 * Event description: User interacts with Upgrade Pop Up which is shown after payment method is successfully connected: close or click Upgrade
 *
 * @param {upgradePopupPaymentTabInteractionParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} interaction - close/upgrade
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<upgradePopupPaymentTabInteractionParams>} LogObject<upgradePopupPaymentTabInteractionParams>
 */
export function upgradePopupPaymentTabInteraction(params) {
    return { evid: 151, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelManualReview' event (evid: 431)
 *
 * Event description: Employee click Cancel Manual Review
 *
 * @param {boAccountPageClickCancelManualReviewParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickCancelManualReviewParams>} LogObject<boAccountPageClickCancelManualReviewParams>
 */
export function boAccountPageClickCancelManualReview(params) {
    return { evid: 431, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageUserDrawerClickOnUm' event (evid: 406)
 *
 * Event description: Employee click open in User Manager
 *
 * @param {boSitePageUserDrawerClickOnUmParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {Url} url - user manager url
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageUserDrawerClickOnUmParams>} LogObject<boSitePageUserDrawerClickOnUmParams>
 */
export function boSitePageUserDrawerClickOnUm(params) {
    return { evid: 406, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodePaymentInitializationCompleted' event (evid: 209)
 *
 * Event description: Payment Initialization completed
 *
 * @param {wixCodePaymentInitializationCompletedParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {number} duration - loading time in miliseconds from buyer activity to page started loading
 * @property {string} errorDesc - error details
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {boolean} status - creation status: true / false
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodePaymentInitializationCompletedParams>} LogObject<wixCodePaymentInitializationCompletedParams>
 */
export function wixCodePaymentInitializationCompleted(params) {
    return { evid: 209, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'installmentBannerLoadFinished' event (evid: 258)
 *
 * Event description: Widget with data about installments ( Afterpay, laybuy, etc) is loaded successfully
 *
 * @param {installmentBannerLoadFinishedParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} offerId - offered payment methods that were shown (array)
 * @property {string} paymentProvider - offered payment providers that were shown (array)
 * @property {string} productId - product id
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<installmentBannerLoadFinishedParams>} LogObject<installmentBannerLoadFinishedParams>
 */
export function installmentBannerLoadFinished(params) {
    return { evid: 258, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutValidationErrors' event (evid: 197)
 *
 * Event description: Send BI event if UI validation of checkout mandatory fields fails
 *
 * @param {checkoutValidationErrorsParams} params - The parameters for this event
 * @property {GUID} appId - vertical ID
 * @property {string} errorMessage - Error message/localization key that was shown
 * @property {string} fieldName - Field type/name
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} paymentMethod - paymentMethod
 * @property {GUID} sessionId - session_id
 * @property {string} validValue - Validation rule/desired value
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutValidationErrorsParams>} LogObject<checkoutValidationErrorsParams>
 */
export function checkoutValidationErrors(params) {
    return { evid: 197, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantCompleteSetUpClicked' event (evid: 131)
 *
 * Event description: Merchant-click complete setup
 *
 * @param {merchantCompleteSetUpClickedParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} notificationType - notification name
 * @property {string} origin - payment page / autoonboarding / active on checkout
 * @property {string} pageType - page
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantCompleteSetUpClickedParams>} LogObject<merchantCompleteSetUpClickedParams>
 */
export function merchantCompleteSetUpClicked(params) {
    return { evid: 131, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletCreditCardSetPrimaryClicked' event (evid: 225)
 *
 * Event description: Logged in member confirms setting credit card as primary one
 *
 * @param {myWalletCreditCardSetPrimaryClickedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} cardId - card_id
 * @property {boolean} isPartner
 * @property {boolean} isRetry - true if it's retry
 * @property {GUID} member_id - -
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitorId
 * @return {LogObject<myWalletCreditCardSetPrimaryClickedParams>} LogObject<myWalletCreditCardSetPrimaryClickedParams>
 */
export function myWalletCreditCardSetPrimaryClicked(params) {
    return { evid: 225, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantManageAccountClicked' event (evid: 118)
 *
 * Event description: Click manage in Payment Methods Page
 *
 * @param {merchantManageAccountClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} avalableCategory - available payment methods
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} paymentCategory - connected payment methods (credit card etc.)
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantManageAccountClickedParams>} LogObject<merchantManageAccountClickedParams>
 */
export function merchantManageAccountClicked(params) {
    return { evid: 118, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXRefundPayout' event (evid: 428)
 *
 * Event description: Employee click   [x]  Refund Payout
 *
 * @param {boAccountPageClickXRefundPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickXRefundPayoutParams>} LogObject<boAccountPageClickXRefundPayoutParams>
 */
export function boAccountPageClickXRefundPayout(params) {
    return { evid: 428, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantViewWixPaymentsNotifications' event (evid: 290)
 *
 * Event description: Merchant saw Wix Payments Notifications (Complete your setup to get verified, Your account is suspended, etc)
 *
 * @param {merchantViewWixPaymentsNotificationsParams} params - The parameters for this event
 * @property {string} account_id - Wix Payments account_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} notificationBody - text of notification
 * @property {string} notificationType - COMPLIANCE_NOT_FINISHED_V4, etc
 * @property {string} pageType - page
 * @property {string} paymentCategory - creditCard, applePay, POS
 * @property {string} paymentProvider - com.wix.wixpay.us
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitorid
 * @return {LogObject<merchantViewWixPaymentsNotificationsParams>} LogObject<merchantViewWixPaymentsNotificationsParams>
 */
export function merchantViewWixPaymentsNotifications(params) {
    return { evid: 290, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutBuyerViewsOrderDetails' event (evid: 204)
 *
 * Event description: Wix Code Check out - Buyer views Order details
 *
 * @param {wixCodeCheckOutBuyerViewsOrderDetailsParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} direction - NOT IN USE: button name (incl. close)
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} page - name of the page
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} selectedCountry - country chosen by buyer
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodeCheckOutBuyerViewsOrderDetailsParams>} LogObject<wixCodeCheckOutBuyerViewsOrderDetailsParams>
 */
export function wixCodeCheckOutBuyerViewsOrderDetails(params) {
    return { evid: 204, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXUnsuspendAccount' event (evid: 420)
 *
 * Event description: Employee click   [x] unsuspend account
 *
 * @param {boAccountPageClickXUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickXUnsuspendAccountParams>} LogObject<boAccountPageClickXUnsuspendAccountParams>
 */
export function boAccountPageClickXUnsuspendAccount(params) {
    return { evid: 420, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantActivateProviderOnCheckOutPopUpShown' event (evid: 173)
 *
 * Event description: When user has completed APM account and tries to make it visible on a check-out by turning on toggle, Activate pop-up is shown
 *
 * @param {merchantActivateProviderOnCheckOutPopUpShownParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - active on check out
 * @property {string} paymentCategory - payment_category
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<merchantActivateProviderOnCheckOutPopUpShownParams>} LogObject<merchantActivateProviderOnCheckOutPopUpShownParams>
 */
export function merchantActivateProviderOnCheckOutPopUpShown(params) {
    return { evid: 173, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boletoBankLogin' event (evid: 233)
 *
 * Event description: When User choose bank and click to login
 *
 * @param {boletoBankLoginParams} params - The parameters for this event
 * @property {number} amountLong - amount
 * @property {string} bankName - Choosed bank
 * @property {DateTime} due_date - due_date on boleto
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boletoBankLoginParams>} LogObject<boletoBankLoginParams>
 */
export function boletoBankLogin(params) {
    return { evid: 233, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardTransactionSelected' event (evid: 160)
 *
 * Event description: user selects transaction to view details
 *
 * @param {paymentDashboardTransactionSelectedParams} params - The parameters for this event
 * @property {string} action - expand / collapse
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} transactionId - transaction_id
 * @property {string} transactionStatus - current transaction status
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardTransactionSelectedParams>} LogObject<paymentDashboardTransactionSelectedParams>
 */
export function paymentDashboardTransactionSelected(params) {
    return { evid: 160, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelRefundPayout' event (evid: 427)
 *
 * Event description: Employee click Cancel   Refund Payout
 *
 * @param {boAccountPageClickCancelRefundPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickCancelRefundPayoutParams>} LogObject<boAccountPageClickCancelRefundPayoutParams>
 */
export function boAccountPageClickCancelRefundPayout(params) {
    return { evid: 427, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantConnectMeClicked' event (evid: 128)
 *
 * Event description: Merchant clicks connect me in smartOB funnel (welcome page or page with existing connections)
 *
 * @param {merchantConnectMeClickedParams} params - The parameters for this event
 * @property {GUID} appId - cashier app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} connectionType - auto/manual - connection type for each of defined providers, according to categories order, separated by comma
 * @property {string} emailStatus - default/changed/not required - tells user changed default (wix) email to create deferred account
 * @property {boolean} isAdditionalStep - false if additional screen for confirming emails wasn't shown; else true; null for is_first = false
 * @property {boolean} isFirst - true = if connection was made from initial page (with no connected pm)
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} pageType - page name
 * @property {string} paymentProvider - list of providers, defined as default - for each category, according to categories order, separated by comma
 * @property {string} place - place where action happened
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} selectedValue - list of categories, selected by a user to connect, separated by comma
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantConnectMeClickedParams>} LogObject<merchantConnectMeClickedParams>
 */
export function merchantConnectMeClicked(params) {
    return { evid: 128, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'upgradePopupPaymentTabShown' event (evid: 150)
 *
 * Event description: Cashier Upgrade Pop Up is shown to user after payment method is successfully connected
 *
 * @param {upgradePopupPaymentTabShownParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<upgradePopupPaymentTabShownParams>} LogObject<upgradePopupPaymentTabShownParams>
 */
export function upgradePopupPaymentTabShown(params) {
    return { evid: 150, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbShippingMethodNotAccepted' event (evid: 217)
 *
 * Event description: if Shipping address is not OK (errors: expected -  SHIPPING_CONTACT_INVALID, SHIPPING_ADDRESS_UNSERVICEABLE, SHIPPING_UNKNOWN)
 *
 * @param {spbShippingMethodNotAcceptedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {number} amountLong - new amount ( * 100 000)
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - new value
 * @property {string} errorReason - in case of 'UNEXPECTED', it is response from vertical
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {string} fieldChanged - field which was changed on payment method page
 * @property {boolean} isPartner
 * @property {GUID} msid - site id
 * @property {string} paymentMethod - new method, when method was changed
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbShippingMethodNotAcceptedParams>} LogObject<spbShippingMethodNotAcceptedParams>
 */
export function spbShippingMethodNotAccepted(params) {
    return { evid: 217, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickSaveEmailOnSmartOb' event (evid: 136)
 *
 * Event description: Merchant-click on save email on smart OB
 *
 * @param {merchantClickSaveEmailOnSmartObParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantClickSaveEmailOnSmartObParams>} LogObject<merchantClickSaveEmailOnSmartObParams>
 */
export function merchantClickSaveEmailOnSmartOb(params) {
    return { evid: 136, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantManagePageLoadStart' event (evid: 245)
 *
 * @param {merchantManagePageLoadStartParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} paymentMethod
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantManagePageLoadStartParams>} LogObject<merchantManagePageLoadStartParams>
 */
export function merchantManagePageLoadStart(params) {
    return { evid: 245, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'prohibitedProductsPageWasShown' event (evid: 239)
 *
 * Event description: Prohibited products page was shown to user after a click on Connect Wix Payment
 *
 * @param {prohibitedProductsPageWasShownParams} params - The parameters for this event
 * @property {string} businessLocation
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} origin - add_payment_method/connect_me/change_provider
 * @property {string} pageType - prohibited_products, products_list
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} selectedValue - list of categories, selected by a user to connect, separated by comma
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<prohibitedProductsPageWasShownParams>} LogObject<prohibitedProductsPageWasShownParams>
 */
export function prohibitedProductsPageWasShown(params) {
    return { evid: 239, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'completeProviderSetupPopupShown' event (evid: 152)
 *
 * Event description: To be sent when at least one payment method doesn't connect automatically
 *
 * @param {completeProviderSetupPopupShownParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - MSID
 * @property {string} origin - autoonboarding / add provider / active on check out
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - VisitoID
 * @return {LogObject<completeProviderSetupPopupShownParams>} LogObject<completeProviderSetupPopupShownParams>
 */
export function completeProviderSetupPopupShown(params) {
    return { evid: 152, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkOutNoAvailableProvidersRefreshClicked' event (evid: 175)
 *
 * Event description: When buyer comes to Payment Component on a check-out, and all of connected providers are disabled, we show notification to a buyer and buyer clicks Refresh
 *
 * @param {checkOutNoAvailableProvidersRefreshClickedParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} buyerComponent - buyer_component
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentCategory - Offline payment, MercadoPago, Paypal, Credit card
 * @property {string} paymentProvider - payment_provider
 * @property {boolean} refreshResult - true if payment component was loaded
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkOutNoAvailableProvidersRefreshClickedParams>} LogObject<checkOutNoAvailableProvidersRefreshClickedParams>
 */
export function checkOutNoAvailableProvidersRefreshClicked(params) {
    return { evid: 175, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boletoPopUpOpened' event (evid: 235)
 *
 * Event description: When User choose pay with boleto and click place order
 *
 * @param {boletoPopUpOpenedParams} params - The parameters for this event
 * @property {number} amountLong - amount
 * @property {DateTime} due_date - due_date on boleto
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boletoPopUpOpenedParams>} LogObject<boletoPopUpOpenedParams>
 */
export function boletoPopUpOpened(params) {
    return { evid: 235, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantUserClickedCta' event (evid: 249)
 *
 * Event description: User clicked cta on modal on accept_payments / manage_page / third_party_cc_providers
 *
 * @param {merchantUserClickedCtaParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} cta - keep / disable
 * @property {boolean} isPartner
 * @property {string} modal_name - leave_without_changes/ confirm_disable_payment_method
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} notificationType - notification name
 * @property {string} page - disconnect / connect other
 * @property {string} paymentCategory - currently connected category
 * @property {string} paymentProvider - currently connected provider
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantUserClickedCtaParams>} LogObject<merchantUserClickedCtaParams>
 */
export function merchantUserClickedCta(params) {
    return { evid: 249, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSetUpAccountClicked' event (evid: 153)
 *
 * Event description: Merchant click on setup account to activate in smart ob - different sources
 *
 * @param {merchantSetUpAccountClickedParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - autoconnect / add provider / set up / set up to activate / change provider
 * @property {string} paymentCategory - Credit Card,PayPal,Mercado Pago, Offline
 * @property {string} paymentProvider - payment_provider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - -
 * @return {LogObject<merchantSetUpAccountClickedParams>} LogObject<merchantSetUpAccountClickedParams>
 */
export function merchantSetUpAccountClicked(params) {
    return { evid: 153, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutPageLoaded' event (evid: 201)
 *
 * Event description: Wix Code Check out - Page loaded
 *
 * @param {wixCodeCheckOutPageLoadedParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} direction - NOT IN USE: button name
 * @property {number} duration - loading time in miliseconds
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {number} fullDuration - full duration from buyer intent to finish loaded
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} page - name of the page
 * @property {string} pageFrom - page buyer is redirected from
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} selectedCountry - country chosen by buyer
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} status - transaction status for thank you page
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodeCheckOutPageLoadedParams>} LogObject<wixCodeCheckOutPageLoadedParams>
 */
export function wixCodeCheckOutPageLoaded(params) {
    return { evid: 201, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletCreditCardDetailsView' event (evid: 222)
 *
 * Event description: Logged in member clicks on credit card to view it's details
 *
 * @param {myWalletCreditCardDetailsViewParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} cardId - card_id
 * @property {boolean} isPartner
 * @property {GUID} member_id - app_instance_id
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitor_id ; starting from June-4th 2020 the value is aid
 * @return {LogObject<myWalletCreditCardDetailsViewParams>} LogObject<myWalletCreditCardDetailsViewParams>
 */
export function myWalletCreditCardDetailsView(params) {
    return { evid: 222, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardRemovedToast' event (evid: 196)
 *
 * Event description: When users, who saw payment dashboard previously, goes to Accept Payment Tab we notify him(through toast) that place changes
 *
 * @param {paymentDashboardRemovedToastParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid - site_id
 * @property {GUID} uuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<paymentDashboardRemovedToastParams>} LogObject<paymentDashboardRemovedToastParams>
 */
export function paymentDashboardRemovedToast(params) {
    return { evid: 196, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickedOnCtaOnConnectPage' event (evid: 252)
 *
 * Event description: Merchant-click complete setup
 *
 * @param {merchantClickedOnCtaOnConnectPageParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} cta - cancel or sign_in
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickedOnCtaOnConnectPageParams>} LogObject<merchantClickedOnCtaOnConnectPageParams>
 */
export function merchantClickedOnCtaOnConnectPage(params) {
    return { evid: 252, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantFullStorySession' event (evid: 466)
 *
 * Event description: Merchant FullStory session recorded
 *
 * @param {merchantFullStorySessionParams} params - The parameters for this event
 * @property {string} experiment - spec name
 * @property {string} fullstoryUrl - url of FullStory recorded session
 * @property {boolean} isPartner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} origin - place user came from
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came to AP
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantFullStorySessionParams>} LogObject<merchantFullStorySessionParams>
 */
export function merchantFullStorySession(params) {
    return { evid: 466, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantOaChoosePaymentMethodsClicked' event (evid: 177)
 *
 * Event description: Merchant clicks Choose Payment Methods on OA Accept Payments Welcome tab
 *
 * @param {merchantOaChoosePaymentMethodsClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantOaChoosePaymentMethodsClickedParams>} LogObject<merchantOaChoosePaymentMethodsClickedParams>
 */
export function merchantOaChoosePaymentMethodsClicked(params) {
    return { evid: 177, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayMerchantValidationError' event (evid: 109)
 *
 * Event description: ApplePay error during startMerchantValidationCheckout
 *
 * @param {applePayMerchantValidationErrorParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} errorReason
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentProvider - authorize.net/Stripe...
 * @property {string} paymentmethod
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayMerchantValidationErrorParams>} LogObject<applePayMerchantValidationErrorParams>
 */
export function applePayMerchantValidationError(params) {
    return { evid: 109, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'transaction3dsForm' event (evid: 193)
 *
 * Event description: when users click "Submit Order" and 3DS form was showed (in case flow_type = 'full redirect' we have only is_Opened = true)
 *
 * @param {transaction3dsFormParams} params - The parameters for this event
 * @property {string} amount - checkout amount
 * @property {string} appId - app_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} flowType - type of 3ds flow: modal, new tab, full page redirect
 * @property {boolean} isFinished - true if page was success , false if not
 * @property {boolean} isManualClose - buyer closed modal
 * @property {boolean} isOpened - true if page was open , false if closed
 * @property {boolean} isPartner
 * @property {boolean} isUserChallenged - mark if uou was challenged
 * @property {GUID} msid
 * @property {GUID} orderId - order_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentMethod - payment method (in case of CreditCard - 3ds challenge)
 * @property {string} paymentProvider - com.stripe/square/wp
 * @property {string} result - result of undergoing 3ds flow
 * @property {GUID} transactionId - transaction_id if it exists
 * @property {number} version3ds - 3DS version (1 or 2)
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<transaction3dsFormParams>} LogObject<transaction3dsFormParams>
 */
export function transaction3dsForm(params) {
    return { evid: 193, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXManualReview' event (evid: 432)
 *
 * Event description: Employee click   [x]  Manual Review
 *
 * @param {boAccountPageClickXManualReviewParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenantid
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickXManualReviewParams>} LogObject<boAccountPageClickXManualReviewParams>
 */
export function boAccountPageClickXManualReview(params) {
    return { evid: 432, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXProcessPayout' event (evid: 424)
 *
 * Event description: Employee click   [x]  Process Payout
 *
 * @param {boAccountPageClickXProcessPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickXProcessPayoutParams>} LogObject<boAccountPageClickXProcessPayoutParams>
 */
export function boAccountPageClickXProcessPayout(params) {
    return { evid: 424, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantObNavigation' event (evid: 242)
 *
 * Event description: Merchant-clicked
 * “More Payment Methods”
 * ”Third party credit / debit card providers”
 * Back arrow on OB page
 *
 * @param {merchantObNavigationParams} params - The parameters for this event
 * @property {string} cta - back/see_more_options/choose_provider/connect_page
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} pageType - what page in the dashboard : accept_payments / more_payment_methods  / third_party_cc_providers
 * @property {string} paymentCategory - dependent from page where user located
 * @property {string} paymentProvider - dependent from page where user located
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantObNavigationParams>} LogObject<merchantObNavigationParams>
 */
export function merchantObNavigation(params) {
    return { evid: 242, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'ecwGettingPaymentDataFromVertical' event (evid: 458)
 *
 * Event description: Once buyer sees modal with the appropriate payment method and was successfully logged in, we ask vertical for the initial payment data. Any provider, any payment method
 *
 * @param {ecwGettingPaymentDataFromVerticalParams} params - The parameters for this event
 * @property {number} amount - payment amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {number} discountAmount - for step = 'result' and result = 'success': discount amount - multiply by 100,000
 * @property {string} errorReason - for step = 'result' and result = 'failure': error description
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} orderType - for step = 'result' and result = 'success': physical / digital
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {string} productid - product id where button was clicked
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} result - success/failure
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {number} shippingAmount - for step = 'result' and result = 'success': shipping amount - multiply by 100,000
 * @property {string} stepName - step of request: initialization/ result
 * @property {number} taxAmount - for step = 'result' and result = 'success': tax amount - multiply by 100,000
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<ecwGettingPaymentDataFromVerticalParams>} LogObject<ecwGettingPaymentDataFromVerticalParams>
 */
export function ecwGettingPaymentDataFromVertical(params) {
    return { evid: 458, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbErrorDuringStartInitializationCheckout' event (evid: 211)
 *
 * Event description: Smart Payment Button error during start Initialization Checkout (creating order on PayPal Failed or user is not premium). Send the reason in the event
 *
 * @param {spbErrorDuringStartInitializationCheckoutParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} errorReason - Error reason
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {string} productId - product id
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbErrorDuringStartInitializationCheckoutParams>} LogObject<spbErrorDuringStartInitializationCheckoutParams>
 */
export function spbErrorDuringStartInitializationCheckout(params) {
    return { evid: 211, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletCreditCardDeleteConfirmed' event (evid: 223)
 *
 * Event description: Logged in member confirms credit card removing from his wallet
 *
 * @param {myWalletCreditCardDeleteConfirmedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} cardId - card_id
 * @property {boolean} isPartner
 * @property {GUID} member_id - -
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<myWalletCreditCardDeleteConfirmedParams>} LogObject<myWalletCreditCardDeleteConfirmedParams>
 */
export function myWalletCreditCardDeleteConfirmed(params) {
    return { evid: 223, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantCancelChangeBusinessLocation' event (evid: 134)
 *
 * Event description: Merchant -cancel change  business location
 *
 * @param {merchantCancelChangeBusinessLocationParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} businessLocation - the country that the user selected
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} origin - seamless/classic
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} type - first_entry/remove_pm/country_list
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantCancelChangeBusinessLocationParams>} LogObject<merchantCancelChangeBusinessLocationParams>
 */
export function merchantCancelChangeBusinessLocation(params) {
    return { evid: 134, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'expressCheckoutWidgetLoadingStart' event (evid: 261)
 *
 * Event description: When express widget loading start
 *
 * @param {expressCheckoutWidgetLoadingStartParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} origin - page type where widget was loaded
 * @property {string} productId - product_id where widget is loaded
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {GUID} visitorId
 * @property {string} widgetId - the origin of stores' pages where ECW appears
 * @return {LogObject<expressCheckoutWidgetLoadingStartParams>} LogObject<expressCheckoutWidgetLoadingStartParams>
 */
export function expressCheckoutWidgetLoadingStart(params) {
    return { evid: 261, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayMerchantValidationSucceeded' event (evid: 111)
 *
 * Event description: ApplePay success Validation Checkout (user is able to login/select payment method)
 *
 * @param {applePayMerchantValidationSucceededParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentProvider
 * @property {string} paymentmethod
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayMerchantValidationSucceededParams>} LogObject<applePayMerchantValidationSucceededParams>
 */
export function applePayMerchantValidationSucceeded(params) {
    return { evid: 111, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSwitchAccountClicked' event (evid: 176)
 *
 * Event description: Merchant clicks on Switch Account on Accept Payments
 *
 * @param {merchantSwitchAccountClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} stepNext - redirect / confirmation popup for trx / manaege popup
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantSwitchAccountClickedParams>} LogObject<merchantSwitchAccountClickedParams>
 */
export function merchantSwitchAccountClicked(params) {
    return { evid: 176, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickOnOpenInWpDashboard' event (evid: 416)
 *
 * Event description: Employee click Open in WP Dashboard
 *
 * @param {boAccountPageClickOnOpenInWpDashboardParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {Url} url - WP dashboard url
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickOnOpenInWpDashboardParams>} LogObject<boAccountPageClickOnOpenInWpDashboardParams>
 */
export function boAccountPageClickOnOpenInWpDashboard(params) {
    return { evid: 416, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutZipAutoFilledFields' event (evid: 276)
 *
 * Event description: list of fields that was filled by zip
 *
 * @param {checkoutZipAutoFilledFieldsParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {string} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} orderSnapshotId - PbW snapshot id
 * @property {string} value - List of fields that was filled by zip. Example "country" or "country, city, address""
 * @property {GUID} visitorId
 * @return {LogObject<checkoutZipAutoFilledFieldsParams>} LogObject<checkoutZipAutoFilledFieldsParams>
 */
export function checkoutZipAutoFilledFields(params) {
    return { evid: 276, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSelectPaymentCategoryToConnect' event (evid: 137)
 *
 * Event description: Merchant-select payment category to connect
 *
 * @param {merchantSelectPaymentCategoryToConnectParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} origin - - not in use
 * @property {string} pageName - Payment Method_first page
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantSelectPaymentCategoryToConnectParams>} LogObject<merchantSelectPaymentCategoryToConnectParams>
 */
export function merchantSelectPaymentCategoryToConnect(params) {
    return { evid: 137, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionDrawerOpen' event (evid: 408)
 *
 * Event description: Employee open transaction drawer
 *
 * @param {boSitePageTransactionDrawerOpenParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageTransactionDrawerOpenParams>} LogObject<boSitePageTransactionDrawerOpenParams>
 */
export function boSitePageTransactionDrawerOpen(params) {
    return { evid: 408, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantProvidersToConnectDefined' event (evid: 185)
 *
 * Event description: Additional event, that describes connection type for providers, defined as default for each of selected categories
 *
 * @param {merchantProvidersToConnectDefinedParams} params - The parameters for this event
 * @property {GUID} appId - cashier app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} connectionType - auto/manual - connection type for each of defined providers, according to categories order, separated by comma
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - list of providers, defined as default - for each category, according to categories order, separated by comma
 * @property {string} selectedValue - list of categories, selected by a user to connect, separated by comma
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantProvidersToConnectDefinedParams>} LogObject<merchantProvidersToConnectDefinedParams>
 */
export function merchantProvidersToConnectDefined(params) {
    return { evid: 185, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnChangeProviderOnThePaymentMethodPage' event (evid: 140)
 *
 * Event description: Merchant-click on change provider on the payment method page. It can be happens from the connected provider or from the link to list providers in +add, if it is add and not change we will get null on payment provider and payment category.
 *
 * @param {merchantClickOnChangeProviderOnThePaymentMethodPageParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isConnect - true if payment method is connected, false if selected
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - current payment provider name
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} source - change provider/ list from +add
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantClickOnChangeProviderOnThePaymentMethodPageParams>} LogObject<merchantClickOnChangeProviderOnThePaymentMethodPageParams>
 */
export function merchantClickOnChangeProviderOnThePaymentMethodPage(params) {
    return { evid: 140, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageUserDrawerOpened' event (evid: 434)
 *
 * Event description: Employee open user drawer
 *
 * @param {boSitePageUserDrawerOpenedParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitor
 * @return {LogObject<boSitePageUserDrawerOpenedParams>} LogObject<boSitePageUserDrawerOpenedParams>
 */
export function boSitePageUserDrawerOpened(params) {
    return { evid: 434, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantBusinessLocationShowHideModalWindow' event (evid: 238)
 *
 * Event description: Merchant-click on country on the Payment first page of the smart onboarding
 *
 * @param {merchantBusinessLocationShowHideModalWindowParams} params - The parameters for this event
 * @property {string} businessLocation - country on the Accept Payment tab
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} paymentMethod - already connected paymentMethods or null
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} type - first_entry/remove_pm
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantBusinessLocationShowHideModalWindowParams>} LogObject<merchantBusinessLocationShowHideModalWindowParams>
 */
export function merchantBusinessLocationShowHideModalWindow(params) {
    return { evid: 238, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'installmentBannerClickedMoreInfo' event (evid: 259)
 *
 * Event description: start loading widget with data about installments ( Afterpay, laybuy, etc)
 *
 * @param {installmentBannerClickedMoreInfoParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} paymentProvider - name of provider
 * @property {string} productId - product id
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<installmentBannerClickedMoreInfoParams>} LogObject<installmentBannerClickedMoreInfoParams>
 */
export function installmentBannerClickedMoreInfo(params) {
    return { evid: 259, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantOaNoProvidersAvailable' event (evid: 178)
 *
 * Event description: When Merchants goes from / select business location with no payment providers, allowing automatically connect, this pop up is shown (only OA)
 *
 * @param {merchantOaNoProvidersAvailableParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} businessLocation - the country that the user selected
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantOaNoProvidersAvailableParams>} LogObject<merchantOaNoProvidersAvailableParams>
 */
export function merchantOaNoProvidersAvailable(params) {
    return { evid: 178, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkOutSubmitOrder' event (evid: 104)
 *
 * Event description: The visitor clicked on 'Submit order'
 *
 * @param {checkOutSubmitOrderParams} params - The parameters for this event
 * @property {GUID} aid - visitor_id from aid
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - -
 * @property {boolean} captchaTokenSent - true if we send captcha token to BE
 * @property {GUID} cardId - -
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {GUID} creditCardId - for previously saved credit cards
 * @property {string} currency - currency
 * @property {string} errorDesc - where is the error
 * @property {boolean} forcedFullRedirectInIAB - Whether full-redirect was forced in InAppBrowser instead of new tab/window strategy
 * @property {boolean} isPartner
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {GUID} membershipId
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {string} paymentCategory - payment_category
 * @property {string} paymentMethodType - For credit card- credit card type:jcb
 * mastercard
 * discover
 * isracard
 * maestro
 * visa
 * amex
 * diners
 * @property {string} paymentProvider - stripe,paypal,square
 * @property {string} redirectTarget - EMBEDDED - modal, NEW_WINDOW - new tab/window, SAME_WINDOW - full redirect
 * @property {boolean} saveCreditCard - true if user choose to save CC
 * @property {string} savePaymentMethodDisplayOption - if  pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {boolean} status - pass client validation
 * @property {GUID} transactionId - transaction_id
 * @property {GUID} visitorId - visitor_id ; starting from June-4th 2020 the value is aid
 * @return {LogObject<checkOutSubmitOrderParams>} LogObject<checkOutSubmitOrderParams>
 */
export function checkOutSubmitOrder(params) {
    return { evid: 104, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boletoPopUpClosed' event (evid: 236)
 *
 * Event description: When User close boleto popup
 *
 * @param {boletoPopUpClosedParams} params - The parameters for this event
 * @property {number} amountLong - amount
 * @property {boolean} close_button - true if button close ,false if [x]
 * @property {DateTime} due_date - due_date on boleto
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boletoPopUpClosedParams>} LogObject<boletoPopUpClosedParams>
 */
export function boletoPopUpClosed(params) {
    return { evid: 236, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantToastBannerShown' event (evid: 467)
 *
 * Event description: Toast banner was shown to the user at Accept payment page
 *
 * @param {merchantToastBannerShownParams} params - The parameters for this event
 * @property {string} bannerName - e.g. back-to-getting-paid
 * @property {boolean} isPartner - is_partner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} place - place where banner is located - accept_payment_top_page,connect_payment_method_modal
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantToastBannerShownParams>} LogObject<merchantToastBannerShownParams>
 */
export function merchantToastBannerShown(params) {
    return { evid: 467, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantTechnicalErrorsInSentry' event (evid: 460)
 *
 * Event description: Errors that appear in Sentry for merchants in the cashier
 *
 * @param {merchantTechnicalErrorsInSentryParams} params - The parameters for this event
 * @property {string} artifact - artifact where error was catched
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} errorMessage - error message
 * @property {boolean} isPartner
 * @property {string} loggingPlatform - sentry, etc
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} pageType - page name
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} sentryEventLink - link to sentry event
 * @property {string} sentryEventId - setry event id
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @property {string} wpAccountId - wix payments account_id
 * @return {LogObject<merchantTechnicalErrorsInSentryParams>} LogObject<merchantTechnicalErrorsInSentryParams>
 */
export function merchantTechnicalErrorsInSentry(params) {
    return { evid: 460, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'financialToolsOpenPaymentsDashboard' event (evid: 191)
 *
 * Event description: Merchant opened Payments Dashboard in Financial Tools
 *
 * @param {financialToolsOpenPaymentsDashboardParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} selectedValue - empty state 1 / empty state 2/ ... / Payments Overview
 * @property {GUID} visitorId
 * @return {LogObject<financialToolsOpenPaymentsDashboardParams>} LogObject<financialToolsOpenPaymentsDashboardParams>
 */
export function financialToolsOpenPaymentsDashboard(params) {
    return { evid: 191, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSaveInManageAccountClicked' event (evid: 144)
 *
 * Event description: Merchant-click complete setup
 *
 * @param {merchantSaveInManageAccountClickedParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} oldValue - old field setting
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} selectedValue - list of additional provider settings e.g. PayPal Credit
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantSaveInManageAccountClickedParams>} LogObject<merchantSaveInManageAccountClickedParams>
 */
export function merchantSaveInManageAccountClicked(params) {
    return { evid: 144, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClicksOnMakeTestTransaction' event (evid: 286)
 *
 * Event description: User clicks on 'Make Test Transaction' on the banner in order to be verified
 *
 * @param {merchantClicksOnMakeTestTransactionParams} params - The parameters for this event
 * @property {string} bannerName - wix_payments_card_notification
 * @property {string} cta - make_test_transaction
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} notificationType - notification name
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitorid
 * @return {LogObject<merchantClicksOnMakeTestTransactionParams>} LogObject<merchantClicksOnMakeTestTransactionParams>
 */
export function merchantClicksOnMakeTestTransaction(params) {
    return { evid: 286, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnCountrySmartOb' event (evid: 132)
 *
 * Event description: Merchant-click on country on the Payment first page of the smart onboarding
 *
 * @param {merchantClickOnCountrySmartObParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} type - first_entry/remove_pm/country_list
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantClickOnCountrySmartObParams>} LogObject<merchantClickOnCountrySmartObParams>
 */
export function merchantClickOnCountrySmartOb(params) {
    return { evid: 132, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantConfirmDisconnectClicked' event (evid: 123)
 *
 * Event description: Confirm disconnect- merchant is asked id he sure that he want to disconnect and click disconnect
 *
 * @param {merchantConfirmDisconnectClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} cta - init_disconnect / ok / cancel
 * @property {string} interaction
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantConfirmDisconnectClickedParams>} LogObject<merchantConfirmDisconnectClickedParams>
 */
export function merchantConfirmDisconnectClicked(params) {
    return { evid: 123, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutErrorOccurredAfterSubmitOrderClicked' event (evid: 455)
 *
 * Event description: When buyers click "Submit Order", but payments don't finish successfully
 *
 * @param {checkoutErrorOccurredAfterSubmitOrderClickedParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} errorDesc - description of error code if exists
 * @property {string} errorReason - error code
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - msid
 * @property {GUID} orderId - order_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentCategory - payment category
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} transactionId - transaction_id if it exists
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutErrorOccurredAfterSubmitOrderClickedParams>} LogObject<checkoutErrorOccurredAfterSubmitOrderClickedParams>
 */
export function checkoutErrorOccurredAfterSubmitOrderClicked(params) {
    return { evid: 455, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickManualReview' event (evid: 429)
 *
 * Event description: Employee click Manual Review in account
 *
 * @param {boAccountPageClickManualReviewParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickManualReviewParams>} LogObject<boAccountPageClickManualReviewParams>
 */
export function boAccountPageClickManualReview(params) {
    return { evid: 429, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'buyerPaymentsComponentLoadStart' event (evid: 125)
 *
 * Event description: Payments component  load start
 *
 * @param {buyerPaymentsComponentLoadStartParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {number} duration - loading time in miliseconds
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} payWith - Payment method
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<buyerPaymentsComponentLoadStartParams>} LogObject<buyerPaymentsComponentLoadStartParams>
 */
export function buyerPaymentsComponentLoadStart(params) {
    return { evid: 125, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantGoToPaymentsDashboardClicked' event (evid: 181)
 *
 * Event description: Merchant clicks on  Go to Payments Dashboard (relevant for wix pay only)
 *
 * @param {merchantGoToPaymentsDashboardClickedParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} connectionStatus - active / suspended / else
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - payment methods / dashboard
 * @property {string} paymentCategory - creditCard
 * @property {string} paymentProvider - wix.com.wixpay
 * @property {GUID} transactionId - null from payment methods
 * @property {string} transactionStatus - null from payment methods
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantGoToPaymentsDashboardClickedParams>} LogObject<merchantGoToPaymentsDashboardClickedParams>
 */
export function merchantGoToPaymentsDashboardClicked(params) {
    return { evid: 181, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'pixModalOpened' event (evid: 287)
 *
 * Event description: When User choose pay with PIX and click place order
 *
 * @param {pixModalOpenedParams} params - The parameters for this event
 * @property {number} amountLong - amount
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {string} paymentMethodType - payment_method_type
 * @property {string} paymentProvider - Payment Provider
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<pixModalOpenedParams>} LogObject<pixModalOpenedParams>
 */
export function pixModalOpened(params) {
    return { evid: 287, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsClickedAtBreadcrumbTrail' event (evid: 215)
 *
 * Event description: Merchant-click  Accept Payments at breadcrumb trail. Please see 85:149 for WP events
 *
 * @param {merchantAcceptPaymentsClickedAtBreadcrumbTrailParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} pageType - what page in the dashboard / origin page
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} targetPage - page where you're going to be redirected
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantAcceptPaymentsClickedAtBreadcrumbTrailParams>} LogObject<merchantAcceptPaymentsClickedAtBreadcrumbTrailParams>
 */
export function merchantAcceptPaymentsClickedAtBreadcrumbTrail(params) {
    return { evid: 215, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantShowOnCheckOutClicked' event (evid: 129)
 *
 * Event description: Merchant-click hide payment provider
 *
 * @param {merchantShowOnCheckOutClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} coupledCategory - paymentCategory which will activate implicitly. i.e. creditCard when turn on WP applePay
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {boolean} showBuyer - true/false
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantShowOnCheckOutClickedParams>} LogObject<merchantShowOnCheckOutClickedParams>
 */
export function merchantShowOnCheckOutClicked(params) {
    return { evid: 129, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'openPopUp' event (evid: 219)
 *
 * Event description: popup was opened on the page
 *
 * @param {openPopUpParams} params - The parameters for this event
 * @property {string} flowType - e.g. cbd flow
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} page - e.g. Attestation form / Accepting Payments / etc.
 * @property {string} popupName - popup name
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<openPopUpParams>} LogObject<openPopUpParams>
 */
export function openPopUp(params) {
    return { evid: 219, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletPageStartedLoading' event (evid: 220)
 *
 * Event description: Logged in member enters My Wallet page
 *
 * @param {myWalletPageStartedLoadingParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {number} hasSavedCreditCards - number of saved credit cards
 * @property {boolean} isPartner
 * @property {GUID} member_id
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<myWalletPageStartedLoadingParams>} LogObject<myWalletPageStartedLoadingParams>
 */
export function myWalletPageStartedLoading(params) {
    return { evid: 220, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantPremiumBannerShown' event (evid: 253)
 *
 * Event description: Upgrade banner was shown to the user at Accept payment page
 *
 * @param {merchantPremiumBannerShownParams} params - The parameters for this event
 * @property {string} bannerName - You're one step closer to selling online, Upgrade with a Business & eCommerce Premium Plan so customers can pay you at checkout
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} place - place where banner is located - accept_payment_top_page,connect_payment_method_modal
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantPremiumBannerShownParams>} LogObject<merchantPremiumBannerShownParams>
 */
export function merchantPremiumBannerShown(params) {
    return { evid: 253, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutPaymentsComponentWasnTShown' event (evid: 454)
 *
 * Event description: Classic Checkout Widget wasn't shown
 *
 * @param {checkoutPaymentsComponentWasnTShownParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} errorDesc - A technical error string.
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentCategory - CreditCard/Ewallet,offline (not avaliable)
 * @property {string} paymentProvider - com.stripe/net.authorize (not avaliable)
 * @property {string} source - 1.pay with, 2.buyer choose, 3.one payment method
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutPaymentsComponentWasnTShownParams>} LogObject<checkoutPaymentsComponentWasnTShownParams>
 */
export function checkoutPaymentsComponentWasnTShown(params) {
    return { evid: 454, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnToastBanner' event (evid: 468)
 *
 * Event description: User clicks on Toast at Accept Payment Page
 *
 * @param {merchantClickOnToastBannerParams} params - The parameters for this event
 * @property {string} bannerName - e.g. back to getting paid
 * @property {string} cta - upgrade, close
 * @property {boolean} isPartner - is partner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} notificationType - notification name
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor id
 * @return {LogObject<merchantClickOnToastBannerParams>} LogObject<merchantClickOnToastBannerParams>
 */
export function merchantClickOnToastBanner(params) {
    return { evid: 468, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageRefundResults' event (evid: 414)
 *
 * Event description: Refund sent and we show result pop-up
 *
 * @param {boSitePageRefundResultsParams} params - The parameters for this event
 * @property {number} amount - amount
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {boolean} status - request status
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageRefundResultsParams>} LogObject<boSitePageRefundResultsParams>
 */
export function boSitePageRefundResults(params) {
    return { evid: 414, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'manualClickOnStartBillingAgreement' event (evid: 114)
 *
 * Event description: Click on Start PayPal review button to enable the Billing agreement option
 *
 * @param {manualClickOnStartBillingAgreementParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} pageType - accept_payments
 * @property {string} paymentCategory - payPal
 * @property {string} paymentProvider - paypal
 * @property {string} platformName - null if desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<manualClickOnStartBillingAgreementParams>} LogObject<manualClickOnStartBillingAgreementParams>
 */
export function manualClickOnStartBillingAgreement(params) {
    return { evid: 114, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnGoToDashboard' event (evid: 270)
 *
 * Event description: Merchant Click on Go to Dashboard at Wix Payments Manage Page
 *
 * @param {merchantClickOnGoToDashboardParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - manage_page
 * @property {string} paymentCategory - creditCard,cardReader etc
 * @property {string} paymentProvider - wixpay_adyen, wixpay_stripe
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickOnGoToDashboardParams>} LogObject<merchantClickOnGoToDashboardParams>
 */
export function merchantClickOnGoToDashboard(params) {
    return { evid: 270, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickViewPayouts' event (evid: 280)
 *
 * Event description: Click on View Payouts at Accept Payments
 *
 * @param {merchantClickViewPayoutsParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - page where user at
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickViewPayoutsParams>} LogObject<merchantClickViewPayoutsParams>
 */
export function merchantClickViewPayouts(params) {
    return { evid: 280, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbButtonSuccessInitializationCheckout' event (evid: 214)
 *
 * Event description: Smart Payment Button success Initialization Checkout (order created on PayPal - user is able to login/select payment method, GooglePay - valid for physical goods, for digital ones is sent by default )
 *
 * @param {spbButtonSuccessInitializationCheckoutParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} ecToken - PayPal token id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbButtonSuccessInitializationCheckoutParams>} LogObject<spbButtonSuccessInitializationCheckoutParams>
 */
export function spbButtonSuccessInitializationCheckout(params) {
    return { evid: 214, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantVerifyAccountClicked284' event (evid: 284)
 *
 * Event description: Click on Verify Account on Accept Payment
 *
 * @param {merchantVerifyAccountClicked284Params} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} notificationType - notification name
 * @property {string} pageType - page where user at
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantVerifyAccountClicked284Params>} LogObject<merchantVerifyAccountClicked284Params>
 */
export function merchantVerifyAccountClicked284(params) {
    return { evid: 284, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickedLinkOnConnectPage' event (evid: 255)
 *
 * Event description: Merchant clicked link on connect page
 *
 * @param {merchantClickedLinkOnConnectPageParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {string} link_name - alias for link on a page
 * @property {string} linkUrl - url where user will be redirected
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} page - Connect page, manage payments, accept payments pages
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickedLinkOnConnectPageParams>} LogObject<merchantClickedLinkOnConnectPageParams>
 */
export function merchantClickedLinkOnConnectPage(params) {
    return { evid: 255, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutBuyerFillsFormFields' event (evid: 449)
 *
 * Event description: Buyer input data in CC form fields (if is_empty = true, we should consider it as is_valid = false)
 *
 * @param {checkoutBuyerFillsFormFieldsParams} params - The parameters for this event
 * @property {string} actionType - type of interaction: start/ finish
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} isEmpty - chosen field is empty or not after interaction (action_type = 'finish')
 * @property {boolean} isPartner
 * @property {boolean} isValid - chosen field is valid or not after interaction (action_type = 'finish')
 * @property {string} mandatoryField - field which is going to be filled/ was filled
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - msid
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {GUID} user_id - uuid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutBuyerFillsFormFieldsParams>} LogObject<checkoutBuyerFillsFormFieldsParams>
 */
export function checkoutBuyerFillsFormFields(params) {
    return { evid: 449, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantConfirmDisconnectPopup' event (evid: 183)
 *
 * Event description: when users wants do disconnect WixPay (directly or because of connecting other provider), we show notification popup
 *
 * @param {merchantConfirmDisconnectPopupParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} interaction - popup shown / disconnect confirmed
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - disconnect / connect other
 * @property {string} paymentCategory - currently connected category
 * @property {string} paymentProvider - currently connected provider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantConfirmDisconnectPopupParams>} LogObject<merchantConfirmDisconnectPopupParams>
 */
export function merchantConfirmDisconnectPopup(params) {
    return { evid: 183, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'expressCheckoutMorePaymentOptionsButtonRendered' event (evid: 456)
 *
 * Event description: 'More payment options' button was render to buyer
 *
 * @param {expressCheckoutMorePaymentOptionsButtonRenderedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - page type where widget was loaded
 * @property {string} productId - product id
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<expressCheckoutMorePaymentOptionsButtonRenderedParams>} LogObject<expressCheckoutMorePaymentOptionsButtonRenderedParams>
 */
export function expressCheckoutMorePaymentOptionsButtonRendered(params) {
    return { evid: 456, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'captchaChallengeFlow' event (evid: 461)
 *
 * Event description: Once bruteforce attack is detected on site, BE sends flag to enable captcha challenge. This event track all action during passing captcha challenge on client side
 *
 * @param {captchaChallengeFlowParams} params - The parameters for this event
 * @property {string} action - all possible action during captcha challenge: captchaLoadStart, captchaLoadEnd, captchaShow, captchaErrorMessage (in case captcha wasn't completed), captchaExpirationMessage, captchaSolve, captchaReset
 * @property {string} amount - checkout amount
 * @property {string} appId - app_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} flowType - type of captcha flow: modal, new tab, full page redirect
 * @property {boolean} isPartner
 * @property {boolean} isTokenExisted - true/false: for action captchaSolve
 * @property {GUID} msid - msid
 * @property {GUID} orderId - order_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentMethod - payment method
 * @property {string} paymentProvider - com.stripe/square/wp
 * @property {boolean} reTry - true/false
 * @property {string} result - result of undergoing 3ds flow
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<captchaChallengeFlowParams>} LogObject<captchaChallengeFlowParams>
 */
export function captchaChallengeFlow(params) {
    return { evid: 461, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickSwitchAccountToggle' event (evid: 433)
 *
 * Event description: Employee  click switch account toggle
 *
 * @param {boAccountPageClickSwitchAccountToggleParams} params - The parameters for this event
 * @property {GUID} accountId - new account_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickSwitchAccountToggleParams>} LogObject<boAccountPageClickSwitchAccountToggleParams>
 */
export function boAccountPageClickSwitchAccountToggle(params) {
    return { evid: 433, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantChooseBusinessLocation' event (evid: 133)
 *
 * Event description: Merchant -choose business location
 *
 * @param {merchantChooseBusinessLocationParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} businessLocation - the country that the user selected
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} origin - seamless/classic
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} type - first_entry/country_list
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantChooseBusinessLocationParams>} LogObject<merchantChooseBusinessLocationParams>
 */
export function merchantChooseBusinessLocation(params) {
    return { evid: 133, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutErrorOccurred' event (evid: 205)
 *
 * Event description: Wix Code Check out - Error occurred
 *
 * @param {wixCodeCheckOutErrorOccurredParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} direction - NOT IN USE: button name (incl. close)
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {string} errorDesc - error details
 * @property {string} errorType - generic, input
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} page - name of the page
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} selectedCountry - country chosen by buyer
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodeCheckOutErrorOccurredParams>} LogObject<wixCodeCheckOutErrorOccurredParams>
 */
export function wixCodeCheckOutErrorOccurred(params) {
    return { evid: 205, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClicksConfirmAtProhibitedProductsPageList' event (evid: 241)
 *
 * Event description: Merchant clicks Confirm at Prohibited products page list
 *
 * @param {merchantClicksConfirmAtProhibitedProductsPageListParams} params - The parameters for this event
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - prohibited_products_list
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} selected_value - values, that user have selected semicolon separated
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClicksConfirmAtProhibitedProductsPageListParams>} LogObject<merchantClicksConfirmAtProhibitedProductsPageListParams>
 */
export function merchantClicksConfirmAtProhibitedProductsPageList(params) {
    return { evid: 241, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardCurrencyChanged' event (evid: 170)
 *
 * Event description: Currency
 *
 * @param {paymentDashboardCurrencyChangedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} changeFromCurrency - change_from_currency
 * @property {string} changeToCurrency - change_to_currency
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardCurrencyChangedParams>} LogObject<paymentDashboardCurrencyChangedParams>
 */
export function paymentDashboardCurrencyChanged(params) {
    return { evid: 170, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'clickDoneOnCompleteSummaryWithoutPremium' event (evid: 187)
 *
 * Event description: The User who already has a premium bought, should see the pop-up with an additional button "Done" and click it
 *
 * @param {clickDoneOnCompleteSummaryWithoutPremiumParams} params - The parameters for this event
 * @property {GUID} appid - appid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} visitorId
 * @return {LogObject<clickDoneOnCompleteSummaryWithoutPremiumParams>} LogObject<clickDoneOnCompleteSummaryWithoutPremiumParams>
 */
export function clickDoneOnCompleteSummaryWithoutPremium(params) {
    return { evid: 187, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'popUpConfigureOtherVerticalsSettings' event (evid: 172)
 *
 * Event description: When user enters Accept Payments Tab in BM, having installed verticals with payment settings, which are not integrated with Cashier, User is shown to Pop-up with links to these verticals. The event shows also User interaction with the pop-up
 *
 * @param {popUpConfigureOtherVerticalsSettingsParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} doNotShowAgain - true if user clicks on "got it" having checked "Don't show again"
 * @property {string} interaction - shown / closed / [vertical] go to link
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} verticalsLinkShown - list of verticals, not integrated with Cashier and with out payment settings
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<popUpConfigureOtherVerticalsSettingsParams>} LogObject<popUpConfigureOtherVerticalsSettingsParams>
 */
export function popUpConfigureOtherVerticalsSettings(params) {
    return { evid: 172, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantChangesSavedSuccessfullyAtManagePage' event (evid: 273)
 *
 * Event description: Merchant Successfully Changed settings at Manage Page
 *
 * @param {merchantChangesSavedSuccessfullyAtManagePageParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {string} accountStatus - accepting_payments, etc
 * @property {string} fields - softDescriptor
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} pageType - manage_page
 * @property {string} paymentCategory - CreditCard,cardReader etc
 * @property {string} paymentProvider - payment_provider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} type - setting types for WixPayments onboarding: Statement| Payout
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @property {string} wpGeo - wp geo
 * @return {LogObject<merchantChangesSavedSuccessfullyAtManagePageParams>} LogObject<merchantChangesSavedSuccessfullyAtManagePageParams>
 */
export function merchantChangesSavedSuccessfullyAtManagePage(params) {
    return { evid: 273, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageLoad' event (evid: 403)
 *
 * Event description: Employee open Site page
 *
 * @param {boSitePageLoadParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageLoadParams>} LogObject<boSitePageLoadParams>
 */
export function boSitePageLoad(params) {
    return { evid: 403, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'expressCheckoutMorePaymentOptionsButtonClicked' event (evid: 282)
 *
 * Event description: When user click on button
 *
 * @param {expressCheckoutMorePaymentOptionsButtonClickedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - page type where widget was loaded
 * @property {string} productId - product id
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<expressCheckoutMorePaymentOptionsButtonClickedParams>} LogObject<expressCheckoutMorePaymentOptionsButtonClickedParams>
 */
export function expressCheckoutMorePaymentOptionsButtonClicked(params) {
    return { evid: 282, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOnPremiumBanner' event (evid: 254)
 *
 * Event description: User clicks on Upgrade Banner at Accept Payment Page
 *
 * @param {merchantClickOnPremiumBannerParams} params - The parameters for this event
 * @property {string} bannerName - You're one step closer to selling online,Upgrade with a Business & eCommerce Premium Plan so customers can pay you at checkout
 * @property {string} cta - upgrade, close
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} notificationType - notification name
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory
 * @property {string} paymentProvider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickOnPremiumBannerParams>} LogObject<merchantClickOnPremiumBannerParams>
 */
export function merchantClickOnPremiumBanner(params) {
    return { evid: 254, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletDeleteCcButtonClicked' event (evid: 452)
 *
 * @param {myWalletDeleteCcButtonClickedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} cardId - card_guid
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} result - Yes/ No (in case there are no active subscriptions related to selected CC)
 * @property {GUID} siteMemberId - member_id
 * @property {string} stepName - start: click on "Delete CC", finish: click on "Yes/No"
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<myWalletDeleteCcButtonClickedParams>} LogObject<myWalletDeleteCcButtonClickedParams>
 */
export function myWalletDeleteCcButtonClicked(params) {
    return { evid: 452, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutBuyerFillsMandatoryFields' event (evid: 440)
 *
 * Event description: Buyer input data in mandatory fields
 *
 * @param {checkoutBuyerFillsMandatoryFieldsParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} isPartner
 * @property {string} mandatoryField - mandatory field which was filled
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - msid
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {GUID} user_id - uuid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutBuyerFillsMandatoryFieldsParams>} LogObject<checkoutBuyerFillsMandatoryFieldsParams>
 */
export function checkoutBuyerFillsMandatoryFields(params) {
    return { evid: 440, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardNavigateToOrderClicked' event (evid: 162)
 *
 * Event description: user clicks on Go to Order
 *
 * @param {paymentDashboardNavigateToOrderClickedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} transactionId - transaction_id
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardNavigateToOrderClickedParams>} LogObject<paymentDashboardNavigateToOrderClickedParams>
 */
export function paymentDashboardNavigateToOrderClicked(params) {
    return { evid: 162, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantMorePaymentOptionsPageShowed' event (evid: 250)
 *
 * Event description: Event includes all pages in Accept Payments component
 *
 * @param {merchantMorePaymentOptionsPageShowedParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} page
 * @property {string} paymentMethod - creditCard,paypal
 * @property {string} paymentProvider
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantMorePaymentOptionsPageShowedParams>} LogObject<merchantMorePaymentOptionsPageShowedParams>
 */
export function merchantMorePaymentOptionsPageShowed(params) {
    return { evid: 250, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'revalidateSessionByWidget' event (evid: 281)
 *
 * Event description: BI event for "revalidate session" call by the Widget to help with investigating a mysterious issue with "changing visitorId"
 *
 * @param {revalidateSessionByWidgetParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {GUID} visitorId
 * @return {LogObject<revalidateSessionByWidgetParams>} LogObject<revalidateSessionByWidgetParams>
 */
export function revalidateSessionByWidget(params) {
    return { evid: 281, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantWixPaymentsLearnMoreClicked' event (evid: 171)
 *
 * Event description: User clicks on Learn More for Wix Payments
 *
 * @param {merchantWixPaymentsLearnMoreClickedParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - Welcome page / Add provider
 * @property {string} paymentProvider - payment provider
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<merchantWixPaymentsLearnMoreClickedParams>} LogObject<merchantWixPaymentsLearnMoreClickedParams>
 */
export function merchantWixPaymentsLearnMoreClicked(params) {
    return { evid: 171, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutPageStartedLoading' event (evid: 200)
 *
 * Event description: Wix Code Check out - Page started loading
 *
 * @param {wixCodeCheckOutPageStartedLoadingParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} direction - NOT IN USE: button name
 * @property {number} duration - loading time in miliseconds from buyer activity to page started loading
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} page - name of the page
 * @property {string} pageFrom - page buyer is redirected from
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} selectedCountry - country chosen by buyer
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} status - transaction status for thank you page
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodeCheckOutPageStartedLoadingParams>} LogObject<wixCodeCheckOutPageStartedLoadingParams>
 */
export function wixCodeCheckOutPageStartedLoading(params) {
    return { evid: 200, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutBuyerChoosesCountry' event (evid: 203)
 *
 * Event description: Wix Code Check out - Buyer chooses country
 *
 * @param {wixCodeCheckOutBuyerChoosesCountryParams} params - The parameters for this event
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} direction - NOT IN USE: button name (incl. close)
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {boolean} isPartner
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} page - name of the page
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} selectedCountry - country chosen by buyer
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<wixCodeCheckOutBuyerChoosesCountryParams>} LogObject<wixCodeCheckOutBuyerChoosesCountryParams>
 */
export function wixCodeCheckOutBuyerChoosesCountry(params) {
    return { evid: 203, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageLoad' event (evid: 415)
 *
 * Event description: Employee open Account page
 *
 * @param {boAccountPageLoadParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageLoadParams>} LogObject<boAccountPageLoadParams>
 */
export function boAccountPageLoad(params) {
    return { evid: 415, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletPageFinishedLoading' event (evid: 221)
 *
 * Event description: Logged in member enters My Wallet page
 *
 * @param {myWalletPageFinishedLoadingParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {number} duration - milliseconds from load started to load finished
 * @property {number} hasSavedCreditCards - number of saved credit cards
 * @property {boolean} isPartner
 * @property {GUID} member_id - -
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {string} paymentAgreementList - List of payment_agreement_id on wallet page
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<myWalletPageFinishedLoadingParams>} LogObject<myWalletPageFinishedLoadingParams>
 */
export function myWalletPageFinishedLoading(params) {
    return { evid: 221, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkOutMandatoryFieldsShown' event (evid: 164)
 *
 * Event description: list of mandatory & CC fields to be fulfilled by buyer
 *
 * @param {checkOutMandatoryFieldsShownParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {GUID} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} creditCardId - credit_card_id
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {string} mandatoryFieldsList - list of mandatory fields to be fulfilled by buyer
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - page_type
 * @property {string} paymentCategory - payment_category
 * @property {string} paymentProvider - payment_provider
 * @property {string} paymentMethod - Offline payment, MercadoPago, Paypal, Credit card
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkOutMandatoryFieldsShownParams>} LogObject<checkOutMandatoryFieldsShownParams>
 */
export function checkOutMandatoryFieldsShown(params) {
    return { evid: 164, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsPageLoadComplete' event (evid: 108)
 *
 * Event description: Merchant click on payment settings in TPA and the cashier payment method page load successfully. In case of wix.numeric--parsing error--duration, please reopen https://jira.wixpress.com/browse/PAY-6934
 *
 * @param {merchantAcceptPaymentsPageLoadCompleteParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} duration - loading time in miliseconds
 * @property {string} flowType - specific flows. e.g. CBD flow
 * @property {boolean} isPartner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} offeredPaymentCategory - array of offered payment_methods
 * @property {string} origin - place user came from
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} paymentCategory - array of connected payment_methods
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came to AP
 * @property {boolean} status - 1-success/ 0-failed
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantAcceptPaymentsPageLoadCompleteParams>} LogObject<merchantAcceptPaymentsPageLoadCompleteParams>
 */
export function merchantAcceptPaymentsPageLoadComplete(params) {
    return { evid: 108, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantGenericErrors' event (evid: 285)
 *
 * Event description: Generic error in BM at payment funnel
 *
 * @param {merchantGenericErrorsParams} params - The parameters for this event
 * @property {string} errorDesc - error description
 * @property {boolean} isPartner
 * @property {string} message - message (full text message)
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} pageType - page where error was appeared
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralinfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantGenericErrorsParams>} LogObject<merchantGenericErrorsParams>
 */
export function merchantGenericErrors(params) {
    return { evid: 285, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'viewPaymentMethodNotification' event (evid: 115)
 *
 * Event description: Merchant view Payments Notifications
 *
 * @param {viewPaymentMethodNotificationParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} notificationBody - text of notification
 * @property {string} notificationKey - notification key
 * @property {string} pageType - page where notification is appeared accept_payments, manage_page etc
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<viewPaymentMethodNotificationParams>} LogObject<viewPaymentMethodNotificationParams>
 */
export function viewPaymentMethodNotification(params) {
    return { evid: 115, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentDashboardSortingApplied' event (evid: 158)
 *
 * Event description: Sorting by date is applied
 *
 * @param {paymentDashboardSortingAppliedParams} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} sortBy - currently date only
 * @property {string} sortOrder - asc / desc
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentDashboardSortingAppliedParams>} LogObject<paymentDashboardSortingAppliedParams>
 */
export function paymentDashboardSortingApplied(params) {
    return { evid: 158, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'braintree3dsForm' event (evid: 450)
 *
 * Event description: 3DS integration for premium flow, which covers only 3DS challenge screen, not modal
 *
 * @param {braintree3dsFormParams} params - The parameters for this event
 * @property {string} appId - app_id (should be Premium)
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} flowType - type of 3ds flow(Should be modal)
 * @property {string} infoThreeDS - threeDSecureInfo from Braintree
 * @property {boolean} isFinished - true if page was success , false if not
 * @property {boolean} isManualClose - buyer closed modal
 * @property {boolean} isOpened - true if page was open , false if closed
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {GUID} orderId - order_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentMethod - payment method (in case of CreditCard - 3ds challenge)
 * @property {string} paymentProvider
 * @property {string} result - result of undergoing 3ds flow
 * @property {GUID} transactionId - transaction_id if it exists
 * @property {number} version3ds - 3DS version (Should be 2)
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<braintree3dsFormParams>} LogObject<braintree3dsFormParams>
 */
export function braintree3dsForm(params) {
    return { evid: 450, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkOutViewTooltip' event (evid: 101)
 *
 * Event description: The visitoer looked at the tooltip
 *
 * @param {checkOutViewTooltipParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} tooltipText - description in tooltip
 * @property {string} tooltipType - card brands/PCI form etc
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkOutViewTooltipParams>} LogObject<checkOutViewTooltipParams>
 */
export function checkOutViewTooltip(params) {
    return { evid: 101, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'ecwPsd2OvercaptureFlow' event (evid: 462)
 *
 * Event description: Transaction was sent to be overcaptured because of changing amount and PSD2 regulation
 *
 * @param {ecwPsd2OvercaptureFlowParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} transactionId - transaction_id
 * @property {string} transactionStatus - transaction_status
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<ecwPsd2OvercaptureFlowParams>} LogObject<ecwPsd2OvercaptureFlowParams>
 */
export function ecwPsd2OvercaptureFlow(params) {
    return { evid: 462, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantPosLearnMoreClicked' event (evid: 188)
 *
 * Event description: User clicks on Learn More for POS (must be linked to KB)
 *
 * @param {merchantPosLearnMoreClickedParams} params - The parameters for this event
 * @property {GUID} appid - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - Welcome page / Add provider
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantPosLearnMoreClickedParams>} LogObject<merchantPosLearnMoreClickedParams>
 */
export function merchantPosLearnMoreClicked(params) {
    return { evid: 188, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkOutNoAvailableProvidersPopUpShown' event (evid: 174)
 *
 * Event description: When buyer comes to Payment Component on a check-out, and all of connected providers are disabled, we show notification to a buyer
 *
 * @param {checkOutNoAvailableProvidersPopUpShownParams} params - The parameters for this event
 * @property {string} appId - vetrical
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} buyerComponent - buyer_component
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentCategory - Offline payment, MercadoPago, Paypal, Credit card
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkOutNoAvailableProvidersPopUpShownParams>} LogObject<checkOutNoAvailableProvidersPopUpShownParams>
 */
export function checkOutNoAvailableProvidersPopUpShown(params) {
    return { evid: 174, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantLearnMoreFromCompleteSetUpBannerClicked' event (evid: 167)
 *
 * Event description: User clicks on Learn More in Complete Set-Up Banner (for providers with deferred account
 *
 * @param {merchantLearnMoreFromCompleteSetUpBannerClickedParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantLearnMoreFromCompleteSetUpBannerClickedParams>} LogObject<merchantLearnMoreFromCompleteSetUpBannerClickedParams>
 */
export function merchantLearnMoreFromCompleteSetUpBannerClicked(params) {
    return { evid: 167, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickSaveAtManagePage' event (evid: 272)
 *
 * Event description: Merchant > Click Save at manage page
 *
 * @param {merchantClickSaveAtManagePageParams} params - The parameters for this event
 * @property {string} fields - softDescriptor
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} pageType - manage_page
 * @property {string} paymentCategory - CreditCard,cardReader etc
 * @property {string} paymentProvider - payment_provider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} type - setting types for WixPayments onboarding: Statement| Payout
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @property {string} wpGeo - wp geo
 * @return {LogObject<merchantClickSaveAtManagePageParams>} LogObject<merchantClickSaveAtManagePageParams>
 */
export function merchantClickSaveAtManagePage(params) {
    return { evid: 272, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickAdyenUrl' event (evid: 439)
 *
 * Event description: Employee  click adyen url
 *
 * @param {boAccountPageClickAdyenUrlParams} params - The parameters for this event
 * @property {GUID} accountId
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant
 * @property {Url} url - Adyen BO url
 * @property {GUID} visitorId - visitor id
 * @return {LogObject<boAccountPageClickAdyenUrlParams>} LogObject<boAccountPageClickAdyenUrlParams>
 */
export function boAccountPageClickAdyenUrl(params) {
    return { evid: 439, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'expressCheckoutWidgetLoadingFinished' event (evid: 262)
 *
 * Event description: When express widget loading finished
 *
 * @param {expressCheckoutWidgetLoadingFinishedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} app_id - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} flowType - Apple JS version
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} origin - page type where widget was loaded
 * @property {string} paymentmethod - list of methods that widget contains
 * @property {string} productId - product_id where widget is loaded
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<expressCheckoutWidgetLoadingFinishedParams>} LogObject<expressCheckoutWidgetLoadingFinishedParams>
 */
export function expressCheckoutWidgetLoadingFinished(params) {
    return { evid: 262, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'paymentsComponentLoadComplete' event (evid: 126)
 *
 * Event description: The payment method pay page is loaded successfully,
 *
 * @param {paymentsComponentLoadCompleteParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {number} duration - loading time in miliseconds
 * @property {string} errorDesc - A technical error string. Empty if load complete successfully
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was loaded for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} payWith - pay_with
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} source - 1.pay with, 2.buyer choose, 3.one payment method
 * @property {boolean} status - true - success / false - failed
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<paymentsComponentLoadCompleteParams>} LogObject<paymentsComponentLoadCompleteParams>
 */
export function paymentsComponentLoadComplete(params) {
    return { evid: 126, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'showClickContactCustomerCareCta' event (evid: 266)
 *
 * Event description: Show / Click "contact customer care" to a user
 *
 * @param {showClickContactCustomerCareCtaParams} params - The parameters for this event
 * @property {string} accountStatus - wix payments account status
 * @property {string} actionType - show / click
 * @property {string} channel - agent / chatbot
 * @property {string} errorMessage - key for message ( kyc / compliance / other)
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} onboardingType - old / new onboarding
 * @property {string} pageType - payments_dashboard / payouts / accept_payments
 * @property {string} place - place on the page (company / personal etc.)
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} url - link to customer care page
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @property {string} wpGeo - GEO
 * @return {LogObject<showClickContactCustomerCareCtaParams>} LogObject<showClickContactCustomerCareCtaParams>
 */
export function showClickContactCustomerCareCta(params) {
    return { evid: 266, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletNewCcWasAdded' event (evid: 446)
 *
 * Event description: New CC was added on wallet page successfully. List of previously saved CC was updated
 *
 * @param {myWalletNewCcWasAddedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {string} paymentAgreementList - List of payment_agreement_id on wallet page
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId - visitorId
 * @return {LogObject<myWalletNewCcWasAddedParams>} LogObject<myWalletNewCcWasAddedParams>
 */
export function myWalletNewCcWasAdded(params) {
    return { evid: 446, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'manualObClickOnUpdateInfo' event (evid: 113)
 *
 * Event description: User clicks on 'Update Info' with Wix Payments accounts
 *
 * @param {manualObClickOnUpdateInfoParams} params - The parameters for this event
 * @property {string} bannerName - i.e. wix_payments_card_notification
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - provider under wp
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<manualObClickOnUpdateInfoParams>} LogObject<manualObClickOnUpdateInfoParams>
 */
export function manualObClickOnUpdateInfo(params) {
    return { evid: 113, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantPaymentMethodPageLoadStart' event (evid: 107)
 *
 * Event description: Merchant click on payment settings in TPA and the cashier payment method page start to load
 *
 * @param {merchantPaymentMethodPageLoadStartParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} duration - loading time in miliseconds
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came to AP
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantPaymentMethodPageLoadStartParams>} LogObject<merchantPaymentMethodPageLoadStartParams>
 */
export function merchantPaymentMethodPageLoadStart(params) {
    return { evid: 107, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundClickCancel' event (evid: 412)
 *
 * Event description: Employee click cancel on refund dialogue
 *
 * @param {boSitePageTransactionRefundClickCancelParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageTransactionRefundClickCancelParams>} LogObject<boSitePageTransactionRefundClickCancelParams>
 */
export function boSitePageTransactionRefundClickCancel(params) {
    return { evid: 412, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'ctaClick' event (evid: 218)
 *
 * Event description: general event for CTA click
 *
 * @param {ctaClickParams} params - The parameters for this event
 * @property {string} cta_value - yes/no/save
 * @property {string} flowType - e.g. cbd flow
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} page - e.g. Attestation form / Accepting Payments / etc.
 * @property {string} popupName - name of popup where click was made
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<ctaClickParams>} LogObject<ctaClickParams>
 */
export function ctaClick(params) {
    return { evid: 218, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantManagePageLoadComplete' event (evid: 246)
 *
 * @param {merchantManagePageLoadCompleteParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {GUID} appId
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} duration - loading time in miliseconds
 * @property {boolean} isPartner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} msid
 * @property {string} namespace - namespace
 * @property {string} paymentMethod - creditCard,paypal
 * @property {string} paymentProvider
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {boolean} status
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantManagePageLoadCompleteParams>} LogObject<merchantManagePageLoadCompleteParams>
 */
export function merchantManagePageLoadComplete(params) {
    return { evid: 246, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmManualReview' event (evid: 430)
 *
 * Event description: Employee click Confirm Manual Review
 *
 * @param {boAccountPageClickConfirmManualReviewParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} category - category
 * @property {number} days - # of days until payout
 * @property {boolean} delayPayouts - true false
 * @property {string} displayName - employee displayName
 * @property {string} finding - finding
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} reason - reason
 * @property {boolean} status - Status
 * @property {string} tenantId - tenant
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickConfirmManualReviewParams>} LogObject<boAccountPageClickConfirmManualReviewParams>
 */
export function boAccountPageClickConfirmManualReview(params) {
    return { evid: 430, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbCountryMissed' event (evid: 289)
 *
 * @param {spbCountryMissedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - new value
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbCountryMissedParams>} LogObject<spbCountryMissedParams>
 */
export function spbCountryMissed(params) {
    return { evid: 289, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletDeleteCcModalResponse' event (evid: 453)
 *
 * Event description: Once Member chose one of the suggested options in "Delete CC" modal: 1) Click “go to my subscriptions”; 2) Click ‘Cancel’
 *
 * @param {myWalletDeleteCcModalResponseParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} cardId - card_guid
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} result - yes: “go to my subscriptions”; no:"cancel"
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<myWalletDeleteCcModalResponseParams>} LogObject<myWalletDeleteCcModalResponseParams>
 */
export function myWalletDeleteCcModalResponse(params) {
    return { evid: 453, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'validationErrorAtManagePage' event (evid: 274)
 *
 * Event description: Validation Error at Manage Page
 *
 * @param {validationErrorAtManagePageParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {string} fields - changed fields (list if more than 1)
 * @property {string} invalidFields - invalid fields in the form (list if more than 1)
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - manage_page
 * @property {string} paymentCategory - CreditCard,cardReader etc
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} visitorId
 * @property {string} wpGeo - wp geo
 * @return {LogObject<validationErrorAtManagePageParams>} LogObject<validationErrorAtManagePageParams>
 */
export function validationErrorAtManagePage(params) {
    return { evid: 274, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbErrorDuringValidationFromVertical' event (evid: 457)
 *
 * Event description: Express Checkout Widget error during validation from vertical (once buyer click on payment method on Express Checkout Widget, the validation from vertical will be held). In case of failure, the reason will be sent in the event
 *
 * @param {spbErrorDuringValidationFromVerticalParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} errorReason - Error reason
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc., G-Pay)
 * @property {string} productId - product id
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbErrorDuringValidationFromVerticalParams>} LogObject<spbErrorDuringValidationFromVerticalParams>
 */
export function spbErrorDuringValidationFromVertical(params) {
    return { evid: 457, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutPaymentComponentCollapsed' event (evid: 182)
 *
 * Event description: Payments Component is collapsed after buyer clicked continue in Stores Checkout
 *
 * @param {checkoutPaymentComponentCollapsedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {boolean} captchaTokenSent - true if we send captcha token to BE
 * @property {string} cardId - card_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {GUID} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {boolean} saveCreditCard - true if buyer wants to save CC
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<checkoutPaymentComponentCollapsedParams>} LogObject<checkoutPaymentComponentCollapsedParams>
 */
export function checkoutPaymentComponentCollapsed(params) {
    return { evid: 182, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'checkoutReturnPaymentAgreement' event (evid: 441)
 *
 * Event description: Checkout widget returns payment agreement: in case creating a new one or assigning previously created
 *
 * @param {checkoutReturnPaymentAgreementParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} isPartner
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {string} paymentProvider - PayPal, Stripe, etc
 * @property {GUID} transactionId - transaction_id in case of creating a new payment agreement
 * @property {GUID} visitorId - visitor_id ; starting from June-4th 2020 the value is aid
 * @return {LogObject<checkoutReturnPaymentAgreementParams>} LogObject<checkoutReturnPaymentAgreementParams>
 */
export function checkoutReturnPaymentAgreement(params) {
    return { evid: 441, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'expressCheckoutWidgetButtonClicked' event (evid: 263)
 *
 * Event description: When user click on button
 *
 * @param {expressCheckoutWidgetButtonClickedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {number} amountLong - amount * 100000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} origin - page type where widget was loaded
 * @property {string} paymentProvider - payment_provider
 * @property {string} paymentmethod - payment method that was choosed
 * @property {string} productId - product_id where widget is loaded
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session for ECW
 * @property {GUID} visitorId
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<expressCheckoutWidgetButtonClickedParams>} LogObject<expressCheckoutWidgetButtonClickedParams>
 */
export function expressCheckoutWidgetButtonClicked(params) {
    return { evid: 263, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundClicked' event (evid: 410)
 *
 * Event description: Employee click refund from drawer
 *
 * @param {boSitePageTransactionRefundClickedParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageTransactionRefundClickedParams>} LogObject<boSitePageTransactionRefundClickedParams>
 */
export function boSitePageTransactionRefundClicked(params) {
    return { evid: 410, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageClickOnTransaction' event (evid: 407)
 *
 * Event description: Employee click on exact transaction
 *
 * @param {boSitePageClickOnTransactionParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId
 * @return {LogObject<boSitePageClickOnTransactionParams>} LogObject<boSitePageClickOnTransactionParams>
 */
export function boSitePageClickOnTransaction(params) {
    return { evid: 407, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantCompleteSetUpBannerShown' event (evid: 166)
 *
 * Event description: In case when deferred account was created, user need to claim it.
 * Banner notifies user
 *
 * @param {merchantCompleteSetUpBannerShownParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantCompleteSetUpBannerShownParams>} LogObject<merchantCompleteSetUpBannerShownParams>
 */
export function merchantCompleteSetUpBannerShown(params) {
    return { evid: 166, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayPaymentAuthorized' event (evid: 265)
 *
 * Event description: When user confirm payment on ApplePay widget
 *
 * @param {applePayPaymentAuthorizedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {number} amountLong - amount * 100000
 * @property {string} appId - app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} orderSnapshotId - cashier order snapshot id
 * @property {string} paymentProvider - payment_provider
 * @property {string} paymentmethod - applepay
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session id
 * @property {GUID} visitorId
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayPaymentAuthorizedParams>} LogObject<applePayPaymentAuthorizedParams>
 */
export function applePayPaymentAuthorized(params) {
    return { evid: 265, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageManualReviewOpened' event (evid: 438)
 *
 * Event description: Employee open manual Review in account
 *
 * @param {boAccountPageManualReviewOpenedParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId - visitor id
 * @return {LogObject<boAccountPageManualReviewOpenedParams>} LogObject<boAccountPageManualReviewOpenedParams>
 */
export function boAccountPageManualReviewOpened(params) {
    return { evid: 438, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boMainPageSearch' event (evid: 401)
 *
 * Event description: Employee input value and clicked 'Search'
 *
 * @param {boMainPageSearchParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} searchType - email, url, msid, uuid, etc
 * @property {string} value - value of search
 * @property {GUID} visitorId
 * @return {LogObject<boMainPageSearchParams>} LogObject<boMainPageSearchParams>
 */
export function boMainPageSearch(params) {
    return { evid: 401, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'creditCardTokenization' event (evid: 447)
 *
 * Event description: Once uou/ user entered CC details and is ready to proceed with payment, payment provider requires tokenize CC
 *
 * @param {creditCardTokenizationParams} params - The parameters for this event
 * @property {string} appId - app_id
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} errorReason - error description for result 'failed'
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} orderId - order_id from vertical
 * @property {string} orderSnapshotId - order_snapshot_id from cashier
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} paymentProvider - com.stripe/square/wp
 * @property {string} result - result of undergoing 3ds flow
 * @property {string} stepName - step of cc tokenization
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<creditCardTokenizationParams>} LogObject<creditCardTokenizationParams>
 */
export function creditCardTokenization(params) {
    return { evid: 447, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantGoToDashboardClicked' event (evid: 256)
 *
 * Event description: Go to Dashboard at Payment Page clicked
 *
 * @param {merchantGoToDashboardClickedParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} paymentCategory - payment methods (sofort/iDeal etc.)
 * @property {string} paymentProvider - Stripe
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantGoToDashboardClickedParams>} LogObject<merchantGoToDashboardClickedParams>
 */
export function merchantGoToDashboardClicked(params) {
    return { evid: 256, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletModalStartedLoading' event (evid: 444)
 *
 * Event description: Modal window started loading on wallet page (depend on modal_type: 'Adding CC', 'Deletion CC')
 *
 * @param {myWalletModalStartedLoadingParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} cardId - card_guid
 * @property {number} hasSavedCreditCards - number of saved credit cards
 * @property {boolean} isPartner
 * @property {string} modalType - modal type: 'Adding CC', 'Deletion CC'
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<myWalletModalStartedLoadingParams>} LogObject<myWalletModalStartedLoadingParams>
 */
export function myWalletModalStartedLoading(params) {
    return { evid: 444, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'myWalletDeleteCcButtonRendered' event (evid: 451)
 *
 * Event description: 'Delete CC' button  was rendered
 *
 * @param {myWalletDeleteCcButtonRenderedParams} params - The parameters for this event
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} cardId - card_guid
 * @property {boolean} isPartner
 * @property {number} loadTime - time from initial to render
 * @property {GUID} msid - msid
 * @property {string} origin - profile / side menu
 * @property {GUID} siteMemberId - member_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitorId
 * @return {LogObject<myWalletDeleteCcButtonRenderedParams>} LogObject<myWalletDeleteCcButtonRenderedParams>
 */
export function myWalletDeleteCcButtonRendered(params) {
    return { evid: 451, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickOrderHardware' event (evid: 279)
 *
 * Event description: Click Order Hardware for POS at Accept Payments
 *
 * @param {merchantClickOrderHardwareParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - page where user at
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} place - place at candyshop
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickOrderHardwareParams>} LogObject<merchantClickOrderHardwareParams>
 */
export function merchantClickOrderHardware(params) {
    return { evid: 279, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantCreateProviderAccountToConnectClicked' event (evid: 117)
 *
 * Event description: click on create a new account on the payment method modal - smartOB - create in the payment method - connect/create account modal
 *
 * @param {merchantCreateProviderAccountToConnectClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantCreateProviderAccountToConnectClickedParams>} LogObject<merchantCreateProviderAccountToConnectClickedParams>
 */
export function merchantCreateProviderAccountToConnectClicked(params) {
    return { evid: 117, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantConnectExistingProviderAccountClicked' event (evid: 116)
 *
 * Event description: click on connect on the payment method modal
 * Merchant gets error when he can't access the payment method ( can't login to the provider) - smartOB - connect in the payment method - connect/create account modal
 *
 * @param {merchantConnectExistingProviderAccountClickedParams} params - The parameters for this event
 * @property {GUID} appId - TPA guid
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} errorDesc - the reason why merchant cant connect to the provider account
 * @property {boolean} isPartner
 * @property {boolean} isValid - 1-success 0- failed
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId - visitor_id
 * @return {LogObject<merchantConnectExistingProviderAccountClickedParams>} LogObject<merchantConnectExistingProviderAccountClickedParams>
 */
export function merchantConnectExistingProviderAccountClicked(params) {
    return { evid: 116, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'applePayButtonRendered' event (evid: 112)
 *
 * Event description: Apple Pay Button was rendered
 *
 * @param {applePayButtonRenderedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} pageType - page where button is appear
 * @property {string} paymentProvider - payment_provider that chosen ( Wixpayments etc.)
 * @property {string} paymentmethod - Apple Pay
 * @property {boolean} requestShipping - if Stores ask about shipping address
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<applePayButtonRenderedParams>} LogObject<applePayButtonRenderedParams>
 */
export function applePayButtonRendered(params) {
    return { evid: 112, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantShowMoreLessProvidersClickedDuringOnboarding' event (evid: 186)
 *
 * Event description: In a new onboarding, we show only major methods on a welcome page. this event tracks when users clicks show more / less methods
 *
 * @param {merchantShowMoreLessProvidersClickedDuringOnboardingParams} params - The parameters for this event
 * @property {string} action - more / less
 * @property {GUID} appId - cashier app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} chosenMethods - list of available payment methods, shown by a default. Separated by comma
 * @property {string} hiddenMethods - list of hidden payment methods, separated by comma
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantShowMoreLessProvidersClickedDuringOnboardingParams>} LogObject<merchantShowMoreLessProvidersClickedDuringOnboardingParams>
 */
export function merchantShowMoreLessProvidersClickedDuringOnboarding(params) {
    return { evid: 186, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boOpenProcessPayoutLightbox' event (evid: 237)
 *
 * Event description: open process payout lightbox
 *
 * @param {boOpenProcessPayoutLightboxParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {number} loadTime - load time
 * @property {GUID} msid - msid
 * @property {string} tenantId - tenant id
 * @property {GUID} visitorId - visitor id
 * @return {LogObject<boOpenProcessPayoutLightboxParams>} LogObject<boOpenProcessPayoutLightboxParams>
 */
export function boOpenProcessPayoutLightbox(params) {
    return { evid: 237, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantClickReviewRequirements' event (evid: 278)
 *
 * Event description: Click Review Requirements for Account at Accept Payments
 *
 * @param {merchantClickReviewRequirementsParams} params - The parameters for this event
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - page where user at
 * @property {string} paymentCategory - payment method
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @return {LogObject<merchantClickReviewRequirementsParams>} LogObject<merchantClickReviewRequirementsParams>
 */
export function merchantClickReviewRequirements(params) {
    return { evid: 278, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'payoutsChangePayoutsFrequency' event (evid: 271)
 *
 * Event description: Change toggle for Payouts frequency in settings
 *
 * @param {payoutsChangePayoutsFrequencyParams} params - The parameters for this event
 * @property {GUID} accountprofileid - account profile id
 * @property {string} accountStatus - accepting_payments, etc
 * @property {number} days - # of weekday / # of day of month
 * @property {boolean} isPartner
 * @property {GUID} msid
 * @property {string} pageType - manage_page
 * @property {string} paymentProvider - com.wix.wixpay.us, etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} referralInfo - place from which user came
 * @property {string} scheduleType - payout schedule = frequency
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} visitorId
 * @property {string} wpGeo - the country that the user selected
 * @return {LogObject<payoutsChangePayoutsFrequencyParams>} LogObject<payoutsChangePayoutsFrequencyParams>
 */
export function payoutsChangePayoutsFrequency(params) {
    return { evid: 271, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'merchantSwitchAccountWithTransactionsConfirmClickedInPopup' event (evid: 179)
 *
 * Event description: When merchant clicks on Switch account, having at least one transaction processed through this account, additional popup is shown. Event means that merchant confirmed this switch.
 *
 * @param {merchantSwitchAccountWithTransactionsConfirmClickedInPopupParams} params - The parameters for this event
 * @property {GUID} appId - cashier app_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {boolean} isPartner
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} paymentCategory - Credit Card
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} userGuid - user_guid
 * @property {GUID} visitorId
 * @return {LogObject<merchantSwitchAccountWithTransactionsConfirmClickedInPopupParams>} LogObject<merchantSwitchAccountWithTransactionsConfirmClickedInPopupParams>
 */
export function merchantSwitchAccountWithTransactionsConfirmClickedInPopup(params) {
    return { evid: 179, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundOpened' event (evid: 435)
 *
 * Event description: Employee open refund from drawer
 *
 * @param {boSitePageTransactionRefundOpenedParams} params - The parameters for this event
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {GUID} transactionid - transaction id
 * @property {GUID} visitorId - visitor id
 * @return {LogObject<boSitePageTransactionRefundOpenedParams>} LogObject<boSitePageTransactionRefundOpenedParams>
 */
export function boSitePageTransactionRefundOpened(params) {
    return { evid: 435, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelProcessPayout' event (evid: 423)
 *
 * Event description: Employee click Cancel  Process Payout
 *
 * @param {boAccountPageClickCancelProcessPayoutParams} params - The parameters for this event
 * @property {GUID} accountId - provider account id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} tenantId
 * @property {GUID} visitorId
 * @return {LogObject<boAccountPageClickCancelProcessPayoutParams>} LogObject<boAccountPageClickCancelProcessPayoutParams>
 */
export function boAccountPageClickCancelProcessPayout(params) {
    return { evid: 423, src: 64, endpoint: 'cashier-ugc', params: params };
}
/**
 * A function to create a log object for the 'spbButtonTrxAuthorized' event (evid: 216)
 *
 * Event description: Transaction successfully authorized and PP modal is closed.
 *
 * @param {spbButtonTrxAuthorizedParams} params - The parameters for this event
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} cartId - cart id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {boolean} isPartner
 * @property {GUID} msid - msid
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {GUID} visitorId - visitor_id
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<spbButtonTrxAuthorizedParams>} LogObject<spbButtonTrxAuthorizedParams>
 */
export function spbButtonTrxAuthorized(params) {
    return { evid: 216, src: 64, endpoint: 'cashier-ugc', params: params };
}
//# sourceMappingURL=index.js.map